import { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import "leaflet-routing-machine";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  MapPin,
  Navigation,
  Trash2,
  Save,
  Plus,
  Clock,
  Route as RouteIcon,
  User,
  Phone,
  Package,
  Calendar,
  FileText,
  Building,
  Building2,
  MailIcon,
} from "lucide-react";
import axiosInstance from "@/api/axiosInstance";

// Fix Leaflet default marker icons
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import GlobalLoader from "./ui/GlobalLoader";

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom marker icons
const pickupIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#F59E0B" stroke="white" stroke-width="2">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const waypointIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="#10B981" stroke="white" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v12m-6-6h12" stroke="white" stroke-width="3"/>
    </svg>
  `),
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
});

const orderIcon = new L.Icon({
  iconUrl:
    "data:image/svg+xml;base64," +
    btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#3B82F6" stroke="white" stroke-width="1">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Create numbered waypoint icons
const createNumberedWaypointIcon = (number: number) =>
  new L.Icon({
    iconUrl:
      "data:image/svg+xml;base64," +
      btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#10B981" stroke="white">
      <circle cx="12" cy="12" r="10"/>
      <text x="12" y="16" text-anchor="middle" fill="white" font-size="10">${number}</text>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Types
interface PickupLocation {
  lat: number;
  lng: number;
  latitude?: number;
  longitude?: number;
  address?: string;
}

interface Order {
  _id: string;
  orderNumber?: string;
  prescriptionNumber?: string;
  pickupLocation?: PickupLocation;
  deliveryLocation?: PickupLocation;
  location?: PickupLocation;
  customerLocation?: PickupLocation;
  address?: string;
  customerAddress?: string;
  customerName?: string;
  customerPhone?: string;
  patientName?: string;
  patientPhone?: string;
  patientAddress?: string;
  deliveryDate?: string;
  deliveryTime?: string;
  priority?: string;
  pickupStatus?:
    | "pending"
    | "pickup-requested"
    | "assigned"
    | "picked-up"
    | "arrived-at-facility"
    | "completed";
  deliveryStatus?:
    | "pending"
    | "planned-for-delivery"
    | "in-route"
    | "delivered"
    | "failed"
    | "cancelled"
    | "completed";
  status?: string; // Keep for backward compatibility
  pharmacyName?: string;
  pharmacyId?: any; // Pharmacy reference (populated or ObjectId)
  totalPrice?: number | string;
}

// Pharmacy type for pickup tab
interface Pharmacy {
  _id?: string;
  id?: string;
  name?: string;
  pharmacyName?: string;
  address?: string;
  phone?: string;
  email?: string;
  licenseNumber?: string;
  location?:
    | { latitude: number; longitude: number; address?: string }
    | { type: string; coordinates: [number, number]; address?: string }
    | any;
  openingTime?: string;
  closingTime?: string;
  shiftTimings?: Array<{
    _id?: string;
    day: string;
    isOpen: boolean;
    openingTime: string;
    closingTime: string;
    shiftTimings: Array<{
      day: string;
      isOpen: boolean;
      openingTime: string;
      closingTime: string;
    }>;
  }>;
}

interface Waypoint {
  lat: number;
  lng: number;
  address?: string;
  orderId?: string;
  orderIds?: string[]; // Multiple order IDs for pharmacy pickups (backward compatibility)
  orders?: any[]; // Array of populated Order objects (NEW)
  pickupOrder?: any[]; // Array of populated PickupOrder objects (NEW)
  type?: "pharmacy" | "delivery" | "other";
  // pharmacy?: {
  //   id: string;
  //   name: string;
  //   phone?: string;
  //   email?: string;
  //   address?: string;
  // };
  pharmacy?: any;
  customer?: {
    name: string;
    phone?: string;
    address?: string;
  };
  shiftTimings?: Array<{
    day: string;
    isOpen: boolean;
    openingTime: string;
    closingTime: string;
  }>;
}

interface RouteData {
  waypoints: Waypoint[];
  distance: number;
  duration: number;
}

// Map Click Handler Component
function MapClickHandler({
  onMapClick,
  isAddingWaypoints,
}: {
  onMapClick: (lat: number, lng: number) => void;
  isAddingWaypoints: boolean;
}) {
  useMapEvents({
    click: (e) => {
      if (isAddingWaypoints) {
        onMapClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

// Map controller component to handle center and zoom changes
function MapController({
  center,
  zoom,
}: {
  center: [number, number];
  zoom: number;
}) {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  
  return null;
}

// Fit bounds controller to show all markers in view
function FitBoundsController({
  locations,
}: {
  locations: Array<{ lat: number; lng: number }>;
}) {
  const map = useMap();
  
  useEffect(() => {
    if (!map || locations.length === 0) return;
    
    // Calculate bounds to fit all locations
    const lats = locations.map((loc) => loc.lat);
    const lngs = locations.map((loc) => loc.lng);
    
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    // Create bounds
    const bounds = L.latLngBounds(
      [minLat, minLng],
      [maxLat, maxLng]
    );
    
    // Fit bounds to map with padding
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [map, locations]);
  
  return null;
}

// Routing Machine Component
function RoutingMachine({
  waypoints,
  onRouteFound,
}: {
  waypoints: Waypoint[];
  onRouteFound: (distance: number, duration: number) => void;
}) {
  const map = useMap();
  const routingControlRef = useRef<any>(null);

  useEffect(() => {
    if (!map || waypoints.length < 2) {
      // Remove existing routing control if waypoints are insufficient
      if (routingControlRef.current) {
        map.removeControl(routingControlRef.current);
        routingControlRef.current = null;
      }
      return;
    }

    // Remove existing routing control
    if (routingControlRef.current) {
      map.removeControl(routingControlRef.current);
      routingControlRef.current = null;
    }

    // Create waypoints for routing
    const routeWaypoints = waypoints.map((wp) => L.latLng(wp.lat, wp.lng));

    // Create new routing control
    const routingControl = (L as any).Routing.control({
      waypoints: routeWaypoints,
      routeWhileDragging: false,
      addWaypoints: false,
      draggableWaypoints: false,
      fitSelectedRoutes: true,
      showAlternatives: false,
      lineOptions: {
        styles: [
          {
            color: "#3B82F6",
            opacity: 0.8,
            weight: 6,
          },
        ],
        extendToWaypoints: true,
        missingRouteTolerance: 0,
      },
      router: (L as any).Routing.osrmv1({
        serviceUrl: "https://router.project-osrm.org/route/v1",
      }),
      createMarker: () => null, // Don't create default markers
    }).addTo(map);

    routingControlRef.current = routingControl;

    // Listen for route found event
    routingControl.on("routesfound", (e: any) => {
      const routes = e.routes;
      if (routes && routes.length > 0) {
        const route = routes[0];
        const distance = route.summary.totalDistance / 1000; // Convert to km
        const duration = route.summary.totalTime / 60; // Convert to minutes
        onRouteFound(distance, duration);
      }
    });

    routingControl.on("routingerror", (e: any) => {
      console.error("Routing error:", e);
      toast.error("Failed to calculate route. Please try different waypoints.");
    });

    // Cleanup
    return () => {
      if (routingControlRef.current) {
        map.removeControl(routingControlRef.current);
        routingControlRef.current = null;
      }
    };
  }, [map, waypoints, onRouteFound]);

  return null;
}

// Main Component
interface MapViewProps {
  onRouteCreated?: () => void;
  selectedRoute?: {
    waypoints?: Array<{
      lat: number;
      lng: number;
      address?: string;
      orderId?: string;
    }>;
    orders?: any[];
    pickupOrder?: any[];
    pharmacies?: any[];
    name?: string;
    description?: string;
    status?: string;
    distance?: number;
    estimatedTime?: number;
  } | null;
  isViewOnly?: boolean;
  mode?: "pickup-orders" | "orders";
}

export function MapView({
  onRouteCreated,
  selectedRoute,
  isViewOnly = false,
  mode = "pickup-orders",
}: MapViewProps = {}) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [isAddingWaypoints, setIsAddingWaypoints] = useState(false);
  const [routeCreated, setRouteCreated] = useState(false);
  const [routeDistance, setRouteDistance] = useState<number>(0);
  const [routeDuration, setRouteDuration] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([
    40.0583, -74.4057,
  ]); // New Jersey default
  const [mapZoom, setMapZoom] = useState<number>(13);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<
    Array<{ display_name: string; lat: string; lon: string }>
  >([]);
  const [searching, setSearching] = useState(false);
  const searchTimeoutRef = useRef<number | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [routeName, setRouteName] = useState<string>("");
  const [tempRouteName, setTempRouteName] = useState<string>("");
  const [currentLocation, setCurrentLocation] = useState<
    [number, number] | null
  >(null);
  const [existingRoutes, setExistingRoutes] = useState<any[]>([]);
  const [detailedOrders, setDetailedOrders] = useState<Map<string, any>>(
    new Map()
  );
  const [pharmacyOrderCounts, setPharmacyOrderCounts] = useState<
    Map<string, number>
  >(new Map());
  const [pharmacyOrders, setPharmacyOrders] = useState<Map<string, string[]>>(
    new Map()
  ); // Store order IDs for each pharmacy
  const [pharmacyPickupOrders, setPharmacyPickupOrders] = useState<
    Map<string, any[]>
  >(new Map()); // Store full pickup order objects

  // Fetch orders/pharmacies and existing routes on mount
  useEffect(() => {
    // Don't fetch data if we're viewing a selected route (data should come from route)
    if (isViewOnly && selectedRoute) {
      console.log("📋 Skipping data fetch - using route data");
      return;
    }
    
    if (mode === "pickup-orders") {
      fetchPharmacies();
    } else {
    fetchOrders();
    }
    fetchExistingRoutes();
  }, [mode, isViewOnly, selectedRoute]);

  // Refresh existing routes when component mounts or when routes are created
  useEffect(() => {
    fetchExistingRoutes();
  }, []);

  // Default map center to user's current location when available
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setMapCenter([latitude, longitude]);
        setCurrentLocation([latitude, longitude]);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  // Center and display selected route when provided
  useEffect(() => {
    if (selectedRoute && isViewOnly) {
      console.log("🗺️ Loading selected route data:", selectedRoute);
      console.log("🗺️ Route waypoints:", selectedRoute.waypoints);
      console.log("🗺️ Route orders:", selectedRoute.orders);
      console.log("🗺️ Route pickupOrder:", (selectedRoute as any).pickupOrder);
      
    const first = selectedRoute?.waypoints?.[0];
      if (
        first &&
        typeof first.lat === "number" &&
        typeof first.lng === "number"
      ) {
      setMapCenter([first.lat, first.lng]);
        
      // Populate waypoints to show the route - preserve all waypoint data including pharmacy/customer info
        const wps = (selectedRoute?.waypoints || []).map((w, idx) => {
          console.log(`🔍 Waypoint ${idx + 1}:`, w);
          console.log(`  - Type: ${(w as any).type}`);
          console.log(`  - Pharmacy:`, (w as any).pharmacy);
          console.log(`  - Customer:`, (w as any).customer);
          console.log(`  - Order IDs (old):`, (w as any).orderIds);
          console.log(`  - Orders (new):`, (w as any).orders);
          console.log(`  - Pickup Orders (new):`, (w as any).pickupOrder);

          // Extract order IDs from orders array or pickupOrder array
          let orderIds = (w as any).orderIds || [];

          // For orders mode: extract IDs from waypoint.orders array
          if ((w as any).orders && Array.isArray((w as any).orders)) {
            orderIds = (w as any).orders.map(
              (order: any) => order._id || order
            );
            console.log(
              `  - Extracted ${orderIds.length} order IDs from waypoint.orders`
            );
          }

          // For pickup-orders mode: extract IDs from waypoint.pickupOrder array
          if ((w as any).pickupOrder && Array.isArray((w as any).pickupOrder)) {
            orderIds = (w as any).pickupOrder.map(
              (order: any) => order._id || order
            );
            console.log(
              `  - Extracted ${orderIds.length} order IDs from waypoint.pickupOrder`
            );
          }
          
          return { 
            lat: w.lat, 
            lng: w.lng,
            address: w.address || "Unknown Address",
            orderId: w.orderId,
            orderIds: orderIds, // Use extracted order IDs
            orders: (w as any).orders, // Keep full orders data
            pickupOrder: (w as any).pickupOrder, // Keep full pickupOrder data
            type: (w as any).type,
            pharmacy: (w as any).pharmacy,
            customer: (w as any).customer,
          } as Waypoint;
        });
        
        if (wps.length > 0) {
        setWaypoints(wps);
        setIsAddingWaypoints(false);
        setRouteCreated(true);
      }
        
        // Populate orders and pharmacies if available in route data
        console.log("📦 Route mode:", mode);
        console.log("📦 Full selectedRoute object:", selectedRoute);
        console.log("📦 Route orders field:", selectedRoute.orders);
        console.log(
          "📦 Route pickupOrder field:",
          (selectedRoute as any).pickupOrder
        );
        
        // Load orders based on the current mode/tab
        let routeOrders = [];
        if (mode === "pickup-orders") {
          // For pickup orders tab, expand pickup orders to show individual orders within them
          // First try waypoint.pickupOrder data (populated)
          const waypointPickupOrders = wps
            .flatMap((wp) => wp.pickupOrder || [])
            .filter(Boolean);

          if (waypointPickupOrders.length > 0) {
            console.log(
              "📦 Pickup Orders tab - Found pickup orders from waypoints:",
              waypointPickupOrders.length
            );
            const expandedOrders = [];
            waypointPickupOrders.forEach((pickupOrder, index) => {
              console.log(`📦 Pickup Order ${index + 1}:`, pickupOrder);

              // If pickupOrder has nested orders, expand them
              if (pickupOrder.orders && Array.isArray(pickupOrder.orders)) {
                console.log(
                  `📦 Pickup Order ${index + 1} contains ${
                    pickupOrder.orders.length
                  } individual orders`
                );
                pickupOrder.orders.forEach((order) => {
                  expandedOrders.push({
                    ...order,
                    pickupOrderInfo: {
                      pickupOrderId: pickupOrder.pickupOrderId,
                      pickupOrderStatus: pickupOrder.status,
                      pharmacyName: pickupOrder.pharmacyName,
                    },
                  });
                });
              } else {
                // If pickupOrder is actually an individual order, use it directly
                expandedOrders.push(pickupOrder);
              }
            });
            routeOrders = expandedOrders;
            console.log(
              "📦 Pickup Orders tab - Expanded to individual orders from waypoints:",
              routeOrders.length
            );
          }
          // Fallback to route-level pickupOrder data
          else if (
            (selectedRoute as any).pickupOrder &&
            Array.isArray((selectedRoute as any).pickupOrder) &&
            (selectedRoute as any).pickupOrder.length > 0
          ) {
            console.log(
              "📦 Pickup Orders tab - Found pickup orders from route:",
              (selectedRoute as any).pickupOrder.length
            );
            
            // Expand pickup orders to show individual orders within them
            const expandedOrders = [];
            (selectedRoute as any).pickupOrder.forEach((pickupOrder, index) => {
              console.log(`📦 Pickup Order ${index + 1}:`, pickupOrder);
              
              // Each pickup order contains multiple individual orders
              if (pickupOrder.orders && Array.isArray(pickupOrder.orders)) {
                console.log(
                  `📦 Pickup Order ${index + 1} contains ${
                    pickupOrder.orders.length
                  } individual orders`
                );
                // Add pickup order info to each individual order
                pickupOrder.orders.forEach((order) => {
                  expandedOrders.push({
                    ...order,
                    pickupOrderInfo: {
                      pickupOrderId: pickupOrder.pickupOrderId,
                      pickupOrderStatus: pickupOrder.status,
                      pharmacyName: pickupOrder.pharmacyName,
                    },
                  });
                });
              }
            });
            
            routeOrders = expandedOrders;
            console.log(
              "📦 Pickup Orders tab - Expanded to individual orders from route:",
              routeOrders.length
            );
          }
        } else if (mode === "orders") {
          // For orders tab, first try waypoint.orders data (populated with pharmacyId)
          const waypointOrders = wps
            .flatMap((wp) => {
              const orders = wp.orders || [];
              // Merge pharmacy info from waypoint into each order
              return orders.map((order) => ({
                ...order,
                // Add pharmacy info from waypoint if not already present in order
                pharmacyId: order.pharmacyId || wp.pharmacy,
                // Ensure we have the waypoint's pharmacy info
                waypointPharmacy: wp.pharmacy,
              }));
            })
            .filter(Boolean);

          if (waypointOrders.length > 0) {
            routeOrders = waypointOrders;
            console.log(
              "📦 Orders tab - Using waypoint.orders field (populated with pharmacy):",
              routeOrders.length
            );
            console.log(
              "📦 First order with pharmacy info:",
              routeOrders[0]?.pharmacyId || routeOrders[0]?.waypointPharmacy
            );
          }
          // Fallback to route-level orders field
          else if (
            selectedRoute.orders &&
            Array.isArray(selectedRoute.orders) &&
            selectedRoute.orders.length > 0
          ) {
            routeOrders = selectedRoute.orders;
            console.log(
              "📦 Orders tab - Using route.orders field:",
              routeOrders.length
            );
          }
        }

        // Distribute orders across waypoints if waypoints don't have orderIds
        if (routeOrders.length > 0 && wps.length > 0) {
          console.log("🔀 Distributing orders across waypoints...");
          const updatedWaypoints = wps.map((waypoint, waypointIndex) => {
            if (!waypoint.orderIds || waypoint.orderIds.length === 0) {
              // Calculate which orders belong to this waypoint
              const ordersPerWaypoint = Math.ceil(
                routeOrders.length / wps.length
              );
              const startIndex = waypointIndex * ordersPerWaypoint;
              const endIndex = Math.min(
                startIndex + ordersPerWaypoint,
                routeOrders.length
              );
              
              // Assign order IDs to this waypoint
              const waypointOrderIds = routeOrders
                .slice(startIndex, endIndex)
                .map((order) => order._id || (order as any).id);
              
              console.log(
                `🔀 Waypoint ${waypointIndex + 1} assigned orders:`,
                waypointOrderIds.length,
                waypointOrderIds
              );
              
              return {
                ...waypoint,
                orderIds: waypointOrderIds
              };
            }
            return waypoint;
          });
          
          // Update waypoints with orderIds
          setWaypoints(updatedWaypoints);
        }
        
        if (routeOrders.length > 0) {
          console.log(
            "📦 Loading route orders for mode",
            mode,
            ":",
            routeOrders.length,
            routeOrders
          );

          // Debug pharmacy info in orders
          routeOrders.forEach((order, idx) => {
            if ((order as any)?.pharmacyId) {
              console.log(`📦 Order ${idx + 1} pharmacy info:`, {
                name:
                  (order as any).pharmacyId.pharmacyName ||
                  (order as any).pharmacyId.name,
                phone: (order as any).pharmacyId.phone,
                id:
                  (order as any).pharmacyId._id || (order as any).pharmacyId.id,
              });
            } else {
              console.log(`📦 Order ${idx + 1} has no pharmacy info`);
            }
          });

          setOrders(routeOrders);
        } else {
          console.log(
            "📦 No orders found for mode",
            mode,
            ", clearing orders state"
          );
          setOrders([]);
        }
        
        if (
          selectedRoute.pharmacies &&
          Array.isArray(selectedRoute.pharmacies)
        ) {
          console.log(
            "🏥 Loading route pharmacies:",
            selectedRoute.pharmacies.length
          );
          setPharmacies(selectedRoute.pharmacies);
        } else {
          // Extract pharmacies from waypoints if not provided at route level
          const uniquePharmacies = new Map();
          wps.forEach(wp => {
            if (wp.pharmacy && typeof wp.pharmacy === 'object') {
              const pharmacyId = wp.pharmacy._id || wp.pharmacy.id;
              if (pharmacyId && !uniquePharmacies.has(pharmacyId)) {
                uniquePharmacies.set(pharmacyId, wp.pharmacy);
              }
            }
          });
          if (uniquePharmacies.size > 0) {
            console.log('🏥 Extracting pharmacies from waypoints:', uniquePharmacies.size);
            setPharmacies(Array.from(uniquePharmacies.values()));
          }
        }
        
        // If no orders in route data, try to fetch them based on waypoint order IDs
        if (routeOrders.length === 0) {
          console.log(
            "📦 No orders in route data, fetching orders based on waypoint order IDs..."
          );
          fetchOrdersForRoute(wps);
        }
      }
    }
  }, [selectedRoute, isViewOnly]);

  // Address search (Nominatim) with debounce
  const searchAddress = (q: string) => {
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    setSearching(true);
    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        q
      )}&limit=5`
    )
      .then((r) => r.json())
      .then((data) => setSuggestions(Array.isArray(data) ? data : []))
      .catch(() => setSuggestions([]))
      .finally(() => setSearching(false));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
    // @ts-ignore
    searchTimeoutRef.current = window.setTimeout(
      () => searchAddress(value),
      400
    );
  };

  const handleSuggestionSelect = (s: {
    display_name: string;
    lat: string;
    lon: string;
  }) => {
    const lat = parseFloat(s.lat);
    const lng = parseFloat(s.lon);
    setMapCenter([lat, lng]);
    setSuggestions([]);
    setSearchQuery(s.display_name);
    // Also add as a waypoint if user is in adding mode
    setWaypoints((prev) => [...prev, { lat, lng, address: s.display_name }]);
  };

  // Fetch orders for a specific route based on waypoint order IDs
  const fetchOrdersForRoute = async (waypoints: Waypoint[]) => {
    try {
      console.log("📦 Fetching orders for route waypoints...");
      
      // Collect all order IDs from waypoints
      const orderIds: string[] = [];
      waypoints.forEach((wp) => {
        if (wp.orderId) {
          orderIds.push(wp.orderId);
        }
        if (wp.orderIds && Array.isArray(wp.orderIds)) {
          orderIds.push(...wp.orderIds);
        }
      });
      
      if (orderIds.length === 0) {
        console.log("📦 No order IDs found in waypoints");
        return;
      }
      
      console.log("📦 Found order IDs:", orderIds);
      
      // Fetch orders by IDs
      const orderPromises = orderIds.map(async (orderId) => {
        try {
          const response = await axiosInstance.get(`/admin/orders/${orderId}`);
          return response.data.data || response.data;
        } catch (error) {
          console.warn(`Failed to fetch order ${orderId}:`, error);
          return null;
        }
      });
      
      const orderResults = await Promise.all(orderPromises);
      const validOrders = orderResults.filter((order) => order !== null);
      
      console.log("📦 Fetched orders for route:", validOrders.length);
      setOrders(validOrders);
    } catch (error) {
      console.error("Error fetching orders for route:", error);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/admin/orders");
      console.log("🔍 Raw orders API response:", response.data);
      // Handle different response structures
      const ordersData =
        response.data?.data || response.data?.orders || response.data || [];
      console.log("🔍 Processed orders data:", ordersData.length, "orders");
      if (ordersData.length > 0) {
        console.log("🔍 First order structure:", ordersData[0]);
        console.log("🔍 First order pharmacyId:", ordersData[0].pharmacyId);
      }
      
      let filteredOrders = [];
      
      if (mode === "pickup-orders") {
        // Filter orders that have pickup locations and status is pickup-requested (original logic)
        filteredOrders = ordersData.filter((order: Order) => {
          const hasLocation =
            order.pickupLocation ||
            order.deliveryLocation ||
            order.location ||
            order.customerLocation;
          const isPickupRequested =
            order.status?.toLowerCase() === "pickup-requested" ||
                                   order.pickupStatus?.toLowerCase() === "pickup-requested";
          return hasLocation && isPickupRequested;
        });
        console.log("Fetched pickup orders:", filteredOrders.length);
      } else if (mode === "orders") {
        // Filter orders that are ready for delivery (exclude only pending and pickup-requested)
        filteredOrders = ordersData.filter((order: Order) => {
          const hasLocation =
            order.pickupLocation ||
            order.deliveryLocation ||
            order.location ||
            order.customerLocation;
          
          // Exclude only orders that are still in pickup phase
          const status = (
            order.status ||
            order.pickupStatus ||
            ""
          ).toLowerCase();
          const shouldExclude =
            status === "pending" || status === "pickup-requested";

          console.log(
            `Order ${order._id} (${
              order.orderNumber
            }): status=${status}, hasLocation=${!!hasLocation}, shouldExclude=${shouldExclude}`
          );
          
          return hasLocation && !shouldExclude;
        });
        console.log(
          "Fetched orders for delivery:",
          filteredOrders.length,
          "out of",
          ordersData.length,
          "total orders"
        );
      }
      
      setOrders(filteredOrders);
      
      // Focus map on order locations
      if (filteredOrders.length > 0) {
        focusMapOnOrders(filteredOrders);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error(
        `Failed to fetch ${
          mode === "pickup-orders" ? "pickup orders" : "orders"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch pharmacies for pickup tab (only those with pickup-requested orders)
  const fetchPharmacies = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/admin/pharmacies");
      const allPharmacies = res.data.data.pharmacies as Pharmacy[];
      
      // Fetch all pickup orders from the backend
      const pickupOrdersRes = await axiosInstance.get(
        "/admin/pickup-orders?status=pickup-requested"
      );
      const allPickupOrders = pickupOrdersRes.data.data || [];
      
      // Filter pharmacies that have pickup-requested orders
      const pharmaciesWithPickups = [];
      const ordersMap = new Map<string, string[]>(); // Store pickup order IDs for each pharmacy
      const pickupOrdersMap = new Map<string, any[]>(); // Store full PickupOrder objects (not Order objects)
      
      for (const pharmacy of allPharmacies) {
        try {
          // Get pickup orders for this pharmacy
          const pharmacyPickupOrders = allPickupOrders.filter((po: any) => {
            const poPharmacyId =
              po.pharmacyId || po.pharmacy?._id || po.pharmacy;
            return poPharmacyId === (pharmacy._id || pharmacy.id);
          });
          
          // Only include pharmacies that have pickup-requested orders
          if (pharmacyPickupOrders.length > 0) {
            console.log(
              `Adding pharmacy ${pharmacy.pharmacyName || pharmacy.name} with ${
                pharmacyPickupOrders.length
              } pickup orders`
            );
            pharmaciesWithPickups.push(pharmacy);
            
            // Store the full PickupOrder objects for this pharmacy
            pickupOrdersMap.set(
              pharmacy._id || pharmacy.id,
              pharmacyPickupOrders
            );
            
            // Store the pickup order IDs for this pharmacy
            const pickupOrderIds = pharmacyPickupOrders
              .map((po: any) => po._id || po.id)
              .filter(Boolean);
            ordersMap.set(pharmacy._id || pharmacy.id, pickupOrderIds);
            console.log(
              `Stored ${pickupOrderIds.length} PICKUP order IDs for pharmacy ${
                pharmacy._id || pharmacy.id
              }:`,
              pickupOrderIds
            );
          }
        } catch (error) {
          console.warn(
            `Failed to check pickup orders for pharmacy ${pharmacy._id}:`,
            error
          );
        }
      }
      
      console.log(
        `Found ${pharmaciesWithPickups.length} pharmacies with pickup requests:`,
        pharmaciesWithPickups.map((p) => p.pharmacyName || p.name)
      );
      
      setPharmacies(pharmaciesWithPickups);
      setPharmacyOrders(ordersMap); // Store the pickup order IDs map
      setPharmacyPickupOrders(pickupOrdersMap); // Store the full PickupOrder objects
      // Fetch order counts for all pharmacies (filtering is done at display time)
      await fetchPharmacyOrderCounts(pharmaciesWithPickups, pickupOrdersMap);
      
      // Focus map on pharmacy locations (all pharmacies, filtering is done at display time)
      if (pharmaciesWithPickups.length > 0) {
        focusMapOnPharmacies(pharmaciesWithPickups);
      }
    } catch (error) {
      console.error("Error fetching pharmacies:", error);
      toast.error("Failed to fetch pharmacies");
    } finally {
      setLoading(false);
    }
  };

  // Fetch order counts for each pharmacy (only pickup-requested orders)
  const fetchPharmacyOrderCounts = async (
    pharmacyData: Pharmacy[],
    pickupOrdersData: Map<string, any[]>
  ) => {
    try {
      const counts = new Map<string, number>();
      
      for (const pharmacy of pharmacyData) {
        try {
          // Get pickup orders from the passed data map
          const pickupOrders =
            pickupOrdersData.get(pharmacy._id || pharmacy.id) || [];
          
          // Count total orders in all pickup requests for this pharmacy
          const totalOrdersCount = pickupOrders.reduce(
            (sum: number, po: any) => {
            return sum + (po.totalOrders || po.orders?.length || 0);
            },
            0
          );

          console.log(
            `Setting count for ${
              pharmacy.pharmacyName || pharmacy.name
            }: ${totalOrdersCount} orders in ${
              pickupOrders.length
            } pickup requests`
          );
          counts.set(pharmacy._id || pharmacy.id, totalOrdersCount);
        } catch (error) {
          console.warn(
            `Failed to count orders for pharmacy ${pharmacy._id}:`,
            error
          );
          counts.set(pharmacy._id || pharmacy.id, 0);
        }
      }
      
      console.log("Final pharmacy order counts:", Array.from(counts.entries()));
      setPharmacyOrderCounts(counts);
    } catch (error) {
      console.error("Error fetching pharmacy order counts:", error);
    }
  };

  // Focus map on pharmacy locations
  const focusMapOnPharmacies = (pharmacyList: Pharmacy[]) => {
    const validLocations: Array<{ lat: number; lng: number }> = [];
    
    pharmacyList.forEach((pharmacy) => {
      const loc = pharmacy?.location;
      let lat: number | undefined;
      let lng: number | undefined;
      
      if (loc) {
        if (
          typeof loc.latitude === "number" &&
          typeof loc.longitude === "number"
        ) {
          lat = loc.latitude;
          lng = loc.longitude;
        } else if (
          Array.isArray(loc?.coordinates) &&
          loc.coordinates.length >= 2
        ) {
          lng = Number(loc.coordinates[0]);
          lat = Number(loc.coordinates[1]);
        }
        
        if (typeof lat === "number" && typeof lng === "number") {
          validLocations.push({ lat, lng });
        }
      }
    });
    
    if (validLocations.length > 0) {
      // Calculate bounds to fit all pharmacy locations
      const lats = validLocations.map((loc) => loc.lat);
      const lngs = validLocations.map((loc) => loc.lng);
      
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      
      // Add padding to bounds
      const latPadding = (maxLat - minLat) * 0.1 || 0.01;
      const lngPadding = (maxLng - minLng) * 0.1 || 0.01;
      
      const bounds = [
        [minLat - latPadding, minLng - lngPadding],
        [maxLat + latPadding, maxLng + lngPadding],
      ];
      
      // Set map center to the center of all locations
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      
      setMapCenter([centerLat, centerLng]);
      setMapZoom(13); // Set a reasonable zoom level
      console.log("Focused map on pharmacy locations:", {
        centerLat,
        centerLng,
        bounds,
        validLocations,
      });
    }
  };

  // Focus map on order locations
  const focusMapOnOrders = (orderList: Order[]) => {
    const validLocations: Array<{ lat: number; lng: number }> = [];
    
    orderList.forEach((order) => {
      const location = getOrderLocation(order, mode);
      if (location) {
        validLocations.push({ lat: location.lat, lng: location.lng });
      }
    });
    
    if (validLocations.length > 0) {
      // Calculate bounds to fit all order locations
      const lats = validLocations.map((loc) => loc.lat);
      const lngs = validLocations.map((loc) => loc.lng);
      
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);
      
      // Add padding to bounds
      const latPadding = (maxLat - minLat) * 0.1 || 0.01;
      const lngPadding = (maxLng - minLng) * 0.1 || 0.01;
      
      // Set map center to the center of all locations
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      
      setMapCenter([centerLat, centerLng]);
      setMapZoom(13); // Set a reasonable zoom level
      console.log("Focused map on order locations:", {
        centerLat,
        centerLng,
        validLocations,
      });
    }
  };

  // Fetch detailed order information
  const fetchDetailedOrder = async (orderId: string) => {
    if (detailedOrders.has(orderId)) {
      return detailedOrders.get(orderId);
    }

    try {
      const res = await axiosInstance.get(`/orders/${orderId}`);
      const detailedOrder = res.data.data;
      setDetailedOrders((prev) => new Map(prev).set(orderId, detailedOrder));
      return detailedOrder;
    } catch (error) {
      console.error("Error fetching detailed order:", error);
      return null;
    }
  };

  const fetchExistingRoutes = async () => {
    try {
      const response = await axiosInstance.get("/routes");
      const routesData =
        response.data?.data || response.data?.routes || response.data || [];
      console.log("📍 Fetched existing routes:", routesData.length, "routes");
      console.log("📍 Routes by type:", {
        "pickup-orders": routesData.filter(
          (r: any) => r.routeType === "pickup-orders"
        ).length,
        orders: routesData.filter((r: any) => r.routeType === "orders").length,
        "no-type": routesData.filter((r: any) => !r.routeType).length,
      });
      console.log("📍 Routes by status:", {
        active: routesData.filter((r: any) => r.status === "active").length,
        completed: routesData.filter((r: any) => r.status === "completed")
          .length,
        pending: routesData.filter((r: any) => r.status === "pending").length,
      });
      setExistingRoutes(routesData);
    } catch (error) {
      console.error("Error fetching existing routes:", error);
    }
  };

  // Get location from order (handles multiple possible field names)
  const getOrderLocation = (
    order: Order,
    mode: "pickup-orders" | "orders"
  ): PickupLocation | null => {
  // For pickup-orders, use pharmacy location (pickup or pharmacy location on order)
    if (mode === "pickup-orders") {
    const loc = order.pickupLocation || order.location; // location presumed to be pharmacy
    if (loc && loc.lat && loc.lng) return { lat: loc.lat, lng: loc.lng };
  } else {
    // For orders, use delivery/customer location
      const loc =
        order.deliveryLocation || order.customerLocation || order.location;
    if (loc && loc.lat && loc.lng) return { lat: loc.lat, lng: loc.lng };
    }
    return null;
  };

  // Check if an order is already included in any existing route (filtered by route type)
  const isOrderInExistingRoute = (order: Order): boolean => {
    const orderLocation = getOrderLocation(order, mode);
    if (!orderLocation) {
      console.log(`❌ Order ${order._id} has no location`);
      return false;
    }

    const foundInRoute = existingRoutes.some((route) => {
      // Filter routes by type: only check routes matching current mode
      // If route has no routeType (old routes), check them only in pickup-orders mode for backward compatibility
      if (route.routeType && route.routeType !== mode) {
        return false;
      }
      if (!route.routeType && mode !== "pickup-orders") {
        return false;
      }
      
      // Skip completed routes for orders mode as well
      if (route.routeType === "orders" && route.status === "completed") {
        console.log(`⏭️ Skipping completed orders route ${route._id}`);
        return false;
      }
      
      if (!route.waypoints || !Array.isArray(route.waypoints)) return false;
      
      // Check if any waypoint in the route matches this order's location
      const matchFound = route.waypoints.some((waypoint: any) => {
        // First try orderId comparison (if backend starts storing it properly)
        if (waypoint.orderId === order._id) {
          console.log(`✅ Order ${order._id} found in route by orderId match`);
          return true;
        }
        
        // Fallback: location-based matching (since orderId is not being stored)
        const latDiff = Math.abs(waypoint.lat - orderLocation.lat);
        const lngDiff = Math.abs(waypoint.lng - orderLocation.lng);
        const tolerance = 0.0001; // ~11 meters tolerance
        
        const isMatch = latDiff < tolerance && lngDiff < tolerance;
        if (isMatch) {
          console.log(
            `✅ Order ${order._id} found in route by location match`,
            { latDiff, lngDiff }
          );
        }
        
        return isMatch;
      });
      
      if (matchFound) {
        console.log(
          `🔍 Order ${order._id} matched in route ${route._id} (type: ${route.routeType}, status: ${route.status})`
        );
      }
      
      return matchFound;
    });

    return foundInRoute;
  };

  // Separate filter function for orders tab - filters out orders that are part of any route
  const isOrderInAnyRoute = (order: Order): boolean => {
    const orderLocation = getOrderLocation(order, mode);
    if (!orderLocation) {
      console.log(`❌ Order ${order._id} has no location`);
      return false;
    }
    
    const foundInRoute = existingRoutes.some((route) => {
      // For orders tab, check ALL routes (both pickup-orders and orders types)
      // Skip completed routes regardless of type
      if (route.status === "completed") {
        console.log(`⏭️ Skipping completed route ${route._id}`);
        return false;
      }
      
      if (!route.waypoints || !Array.isArray(route.waypoints)) return false;
      
      // Check if any waypoint in the route matches this order's location
      const matchFound = route.waypoints.some((waypoint: any) => {
        // First try orderId comparison (if backend starts storing it properly)
        if (waypoint.orderId === order._id) {
          console.log(
            `✅ Order ${order._id} found in route by orderId match (any route)`
          );
          return true;
        }
        
        // Fallback: location-based matching (since orderId is not being stored)
        const latDiff = Math.abs(waypoint.lat - orderLocation.lat);
        const lngDiff = Math.abs(waypoint.lng - orderLocation.lng);
        const tolerance = 0.0001; // ~11 meters tolerance
        
        const isMatch = latDiff < tolerance && lngDiff < tolerance;
        if (isMatch) {
          console.log(
            `✅ Order ${order._id} found in route by location match (any route)`,
            {
            latDiff, 
            lngDiff, 
            routeType: route.routeType,
              routeStatus: route.status,
            }
          );
        }
        
        return isMatch;
      });
      
      if (matchFound) {
        console.log(
          `🔍 Order ${order._id} matched in route ${route._id} (type: ${route.routeType}, status: ${route.status}) - orders tab filter`
        );
      }
      
      return matchFound;
    });

    return foundInRoute;
  };

  // Check if a pharmacy location is already included in any pickup order route
  // Returns true if pharmacy should be hidden, false if it should be shown
  const isPharmacyInExistingRoute = (pharmacy: Pharmacy): boolean => {
    const pharmacyPoint = getPharmacyPickupPoint(pharmacy);
    if (!pharmacyPoint) return false;

    // Check if pharmacy is in any route
    const isInRoute = existingRoutes.some((route) => {
      // Only check pickup-order type routes
      if (
        route.routeType !== "pickup-orders" &&
        route.routeType !== undefined
      ) {
        return false;
      }
      // Skip completed pickup-order routes
      if (route.routeType === "pickup-orders" && route.status === "assigned") {
        return false;
      }
      // If route has no routeType (old routes), include them in the check
      
      if (!route.waypoints || !Array.isArray(route.waypoints)) return false;
      
      // Check if any waypoint in the route matches this pharmacy's location
      return route.waypoints.some((waypoint: any) => {
        // Check if waypoint is a pharmacy type and matches the ID
        if (waypoint.type === "pharmacy" && waypoint.pharmacy) {
          const waypointPharmacyId =
            typeof waypoint.pharmacy === "string"
              ? waypoint.pharmacy
              : waypoint.pharmacy.id;
          if (waypointPharmacyId === (pharmacy._id || pharmacy.id)) return true;
        }
        
        // Fallback: location-based matching
        const latDiff = Math.abs(waypoint.lat - pharmacyPoint.lat);
        const lngDiff = Math.abs(waypoint.lng - pharmacyPoint.lng);
        const tolerance = 0.0001; // ~11 meters tolerance
        
        return latDiff < tolerance && lngDiff < tolerance;
      });
    });

    // If pharmacy is NOT in any route, show it (return false)
    // if (!isInRoute) {
    //   return false;
    // }

    // If pharmacy IS in a route, check if it has pickup orders with isNew: true
    const pharmacyId = pharmacy._id || pharmacy.id;
    const pickupOrders = pharmacyPickupOrders.get(pharmacyId) || [];
    
    console.log(
      `🔍 Checking pharmacy ${pharmacy.pharmacyName || pharmacy.name}:`,
      {
      pharmacyId,
      isInRoute,
      pickupOrdersCount: pickupOrders.length,
        pickupOrders: pickupOrders.map((o) => ({
          id: o._id,
          hasNewOrders: o.hasNewOrders,
        })),
      }
    );

    // Check if any pickup order has isNew: true
    // const hasNewOrders = pickupOrders.some((order: any) => order.isNew === true);
    const hasNewOrders = pickupOrders.some(
      (order: any) => order.hasNewOrders === true
    );
    
    console.log(`  hasNewOrders: ${hasNewOrders}`);

    // If pharmacy has new orders (isNew: true), show it (return false)
    // If all orders have isNew: false or no orders, hide it (return true)
    return !hasNewOrders;
  };
  
  function checkIsInRoute(order, mode) {
    if (mode === "orders") return order.pickupStatus !== "completed" || order.deliveryStatus !== "pending"|| isOrderInAnyRoute(order) || isOrderInExistingRoute(order);
    return ;
  }

  // Get filtered orders (excluding those already in routes)
  const getFilteredOrders = (): Order[] => {
    const filtered = orders.filter((order) => {
      // Use different filter logic based on mode
      // const isInRoute =
      //   mode === "orders"
      //     ? isOrderInAnyRoute(order)
      //     : isOrderInExistingRoute(order);

      const isInRoute = checkIsInRoute(order, mode);

      console.log(
        `Order ${order._id} (${order.orderNumber}): isInRoute=${isInRoute}, mode=${mode}`
      );
      return !isInRoute;
    });
    console.log(
      `Total orders: ${orders.length}, Filtered orders: ${filtered.length}, Mode: ${mode}`
    );
    return filtered;
  };

  // Get filtered pharmacies (excluding those already in pickup order routes)
  const getFilteredPharmacies = (): Pharmacy[] => {
    return pharmacies.filter(
      (pharmacy) => !isPharmacyInExistingRoute(pharmacy)
    );
  };

  // Transform pharmacy to unified marker object
  const getPharmacyPickupPoint = (pharmacy: Pharmacy): Waypoint | null => {
    const loc = pharmacy?.location;
    let lat: number | undefined;
    let lng: number | undefined;
    let address: string | undefined;
    if (!loc) return null;
    if (typeof loc.latitude === "number" && typeof loc.longitude === "number") {
      lat = loc.latitude;
      lng = loc.longitude;
      address = loc.address;
    } else if (Array.isArray(loc?.coordinates) && loc.coordinates.length >= 2) {
      lng = Number(loc.coordinates[0]);
      lat = Number(loc.coordinates[1]);
      address = loc.address;
    }
    if (typeof lat === "number" && typeof lng === "number") {
      return {
        lat,
        lng,
        address:
          address || pharmacy.address || pharmacy.pharmacyName || pharmacy.name,
      };
    }
    return null;
  };

  // For pickup tab, compute unique pharmacy points (one marker per pharmacy) - excludes pharmacies already in routes
  const getPickupPharmacyPoints = (): Array<{
    id: string;
    point: Waypoint;
  }> => {
    const list: Array<{ id: string; point: Waypoint }> = [];
    const seen = new Set<string>();
    const filteredPharmacies = getFilteredPharmacies();
    console.log(
      "getPickupPharmacyPoints - total pharmacies:",
      pharmacies.length,
      "filtered:",
      filteredPharmacies.length
    );
    filteredPharmacies.forEach((p) => {
      const id = (p._id ||
        p.id ||
        (p as any)?.userId ||
        JSON.stringify(p)) as string;
      const pt = getPharmacyPickupPoint(p);
      console.log(
        `Pharmacy ${p.pharmacyName || p.name} location:`,
        p.location,
        "point:",
        pt
      );
      if (!pt) return;
      const key = `${pt.lat.toFixed(5)}_${pt.lng.toFixed(5)}`;
      if (seen.has(key)) return; // ensure one marker per location
      seen.add(key);
      list.push({ id, point: pt });
    });
    console.log("getPickupPharmacyPoints - returning:", list.length, "points");
    return list;
  };

  // Handle marker click - add to waypoints
  const handleMarkerClick = (order: Order) => {
    console.log("🎯🎯🎯 HANDLE MARKER CLICK CALLED 🎯🎯🎯", order);

    if (!isAddingWaypoints) {
      console.log(
        "❌ Not adding waypoints, isAddingWaypoints:",
        isAddingWaypoints
      );
      // toast.info('Click "Create Route" to start adding waypoints');
      return;
    }

    console.log("✅ Adding waypoints mode is active");

    const location = getOrderLocation(order, mode);
    if (!location) return;

    // Check if already added
    const alreadyAdded = waypoints.some((wp) => wp.orderId === order._id);
    if (alreadyAdded) {
      toast.warning("This pickup is already in the route");
      return;
    }

    // Debug: Log order data to see pharmacy info
    console.log("🔍 Creating waypoint from order:", {
      orderId: order._id,
      orderNumber: order.orderNumber,
      pharmacyId: order.pharmacyId,
      pharmacyName: order.pharmacyName,
      fullOrder: order,
    });

    // Extract pharmacy information from populated order
    let pharmacyInfo = undefined;
    if (order.pharmacyId && typeof order.pharmacyId === "object") {
      // Pharmacy is populated - spread all fields including shiftTimings
      pharmacyInfo = {
        ...order.pharmacyId,
        id: order.pharmacyId._id || order.pharmacyId,
        name: order.pharmacyId.pharmacyName || order.pharmacyId.name,
      };
      console.log(
        "✅ Extracted pharmacy info from populated order:",
        pharmacyInfo
      );
    } else if (order.pharmacyName) {
      // Fallback to pharmacy name only
      pharmacyInfo = {
        id: null,
        name: order.pharmacyName,
        phone: null,
        email: null,
        address: null,
      };
      console.log("⚠️ Using pharmacy name fallback:", pharmacyInfo);
    } else {
      console.log("❌ No pharmacy information found in order");
    }

    const newWaypoint: Waypoint = {
      lat: location.lat,
      lng: location.lng,
      address: order.address || order.customerAddress || "Pickup Location",
      orderId: order._id,
      type: mode === "pickup-orders" ? "pharmacy" : "delivery",
      pharmacy: pharmacyInfo,
      // Add customer information
      customer: {
        name: order.customerName,
        phone: order.customerPhone,
        address: order.customerAddress || order.address,
      },
    };

    setWaypoints([...waypoints, newWaypoint]);
    // toast.success(`Added waypoint #${waypoints.length + 1}`);
  };

  // Handle map click - add manual waypoint
  const handleMapClick = (lat: number, lng: number) => {
    const newWaypoint: Waypoint = {
      lat,
      lng,
      address: `Manual Point (${lat.toFixed(4)}, ${lng.toFixed(4)})`,
      type: "other",
    };

    setWaypoints([...waypoints, newWaypoint]);
    console.log("📍 Added manual waypoint:", newWaypoint);
    // toast.success(`Added manual waypoint #${waypoints.length + 1}`);
  };

  // Start creating route
  const handleCreateRoute = () => {
    if (waypoints.length > 0) {
      toast.warning("Clear existing waypoints first");
      return;
    }
    setTempRouteName(
      routeName ||
        `Route ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`
    );
    setCreateDialogOpen(true);
  };

  const confirmCreateRoute = () => {
    const finalName =
      tempRouteName?.trim() ||
      `Route ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
    setRouteName(finalName);
    setCreateDialogOpen(false);
    setIsAddingWaypoints(true);
    setRouteCreated(false);
    // toast.info('Click on markers or map to add waypoints');
  };

  // Generate route
  const handleGenerateRoute = () => {
    // For pickup-orders, allow single waypoint (rider location will be added when rider accepts)
    if (mode === "pickup-orders" && waypoints.length === 1) {
      setRouteCreated(true);
      setIsAddingWaypoints(false);
      // toast.success('Pickup route created! Rider location will be added upon acceptance.');
      return;
    }
    
    // For regular orders, require at least 2 waypoints
    if (waypoints.length < 1) {
      toast.error("Add at least 1 waypoints to generate a route");
      return;
    }
    
    setRouteCreated(true);
    setIsAddingWaypoints(false);
    // toast.success('Generating optimized route...');
  };

  // Clear route
  const handleClearRoute = () => {
    setWaypoints([]);
    setIsAddingWaypoints(false);
    setRouteCreated(false);
    setRouteDistance(0);
    setRouteDuration(0);
    // toast.info('Route cleared');
  };

  // Save route to backend
  const handleSaveRoute = async () => {
    // For pickup-orders, allow single waypoint (rider location added later)
    const minWaypoints = mode === "pickup-orders" ? 1 : 2;
    
    if (!routeCreated || waypoints.length < minWaypoints) {
      toast.error(
        `Generate a route with at least ${minWaypoints} waypoint${
          minWaypoints > 1 ? "s" : ""
        } before saving`
      );
      return;
    }

    try {
      setLoading(true);
      
      console.log("🔍 Debug - Current waypoints state:", waypoints);
      console.log(
        "🔍 Debug - Pharmacy orders map:",
        Array.from(pharmacyOrders.entries())
      );
      
      // Collect all order IDs from waypoints
      const allOrderIds: string[] = [];
      waypoints.forEach((wp, index) => {
        console.log(`🔍 Processing waypoint ${index}:`, {
          type: wp.type,
          orderIds: wp.orderIds,
          orderId: wp.orderId,
          pharmacyId: wp.pharmacy?.id,
        });
        
        // For pharmacy waypoints, add all pickup orders
        if (wp.orderIds && wp.orderIds.length > 0) {
          console.log(
            `✅ Adding ${wp.orderIds.length} order IDs from waypoint ${index}`
          );
          allOrderIds.push(...wp.orderIds);
        } else if (wp.type === "pharmacy" && wp.pharmacy?.id) {
          // Fallback: try to get orders from the map if not in waypoint
          const orderIds = pharmacyOrders.get(wp.pharmacy.id);
          if (orderIds && orderIds.length > 0) {
            console.log(
              `✅ Fallback: Adding ${orderIds.length} order IDs from map for pharmacy ${wp.pharmacy.id}`
            );
            allOrderIds.push(...orderIds);
          }
        }
        
        // For customer waypoints, add the single order
        if (wp.orderId) {
          console.log(`✅ Adding single order ID: ${wp.orderId}`);
          allOrderIds.push(wp.orderId);
        }
      });
      
      console.log("📦 Total collected order IDs:", allOrderIds);
      
      // Convert order IDs to objects with _id field for MongoDB population
      const orderReferences = allOrderIds.map((id) => ({ _id: id }));
      // alert(JSON.stringify(orderReferences));
      
      const routeData: RouteData = {
        waypoints: waypoints.map((wp) => {
          const waypointPayload: any = {
          lat: wp.lat,
          lng: wp.lng,
            address:
              wp.address ||
              wp.customer?.address ||
              wp.pharmacy?.address ||
              "Location",
          type: wp.type,
            routeType: mode, // Add routeType to each waypoint
          };

          // Add pharmacy ID reference if it's a pharmacy waypoint OR if it has pharmacy info
          if (wp.pharmacy?.id) {
            waypointPayload.pharmacy = wp.pharmacy.id;
            console.log(
              `✅ Adding pharmacy ${wp.pharmacy.id} to waypoint payload`
            );
          } else {
            console.log(`⚠️ No pharmacy info found for waypoint:`, {
              type: wp.type,
              orderId: wp.orderId,
          pharmacy: wp.pharmacy,
            });
          }

          // For pickup-orders mode: add pickupOrder array reference to waypoint
          if (mode === "pickup-orders") {
            // If this waypoint has orderIds (pickup orders from pharmacy)
            if (wp.orderIds && wp.orderIds.length > 0) {
              // Add all pickup order IDs to the waypoint as an array
              waypointPayload.pickupOrder = wp.orderIds;
            }
          } else {
            // For orders mode: add orders array reference to waypoint
            if (wp.orderId) {
              // Single order for delivery waypoint
              waypointPayload.orders = [wp.orderId];
            } else if (wp.orderIds && wp.orderIds.length > 0) {
              // Multiple orders
              waypointPayload.orders = wp.orderIds;
            }
          }

          // Keep orderId and orderIds for backward compatibility
          if (wp.orderId) waypointPayload.orderId = wp.orderId;
          if (wp.orderIds) waypointPayload.orderIds = wp.orderIds;

          return waypointPayload;
        }),
        distance: routeDistance,
        duration: routeDuration,
      };
      
      console.log("💾 Saving route with waypoints:", routeData.waypoints);
      console.log("💾 Full route data being sent:", routeData);

      const payload: any = {
        name:
          routeName?.trim() ||
          `Route ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        description:
          mode === "pickup-orders" && waypoints.length === 1
          ? `Pickup route - Rider location pending` 
          : `Auto-generated route with ${waypoints.length} waypoints`,
        waypoints: routeData.waypoints,
        distance: routeData.distance,
        estimatedTime: routeData.duration,
        status:
          mode === "pickup-orders" && waypoints.length === 1
            ? "pending"
            : "active",
        routeType: mode, // 'pickup-orders' or 'orders'
        isPendingRider: mode === "pickup-orders" && waypoints.length === 1, // Flag for single-point pickup routes
      };
      
      // For pickup-orders: send single object, for orders: send array
      if (mode === "pickup-orders") {
        const orderReferences = Array.from(new Set(allOrderIds)).map((id) => ({
          _id: id,
        }));
        if (orderReferences.length > 0) {
          payload.pickupOrder = orderReferences;
          // payload.pickupOrder = [...new Set(orderReferences)];
        } else {
          payload.pickupOrder = null;
          console.log("📦 No pickup orders found, setting pickupOrder to null");
        }
      } else {
      //   // For delivery orders, send as array
        payload.Orders = orderReferences;
        console.log("📦 Adding Orders to payload:", orderReferences);
      }
      
      console.log("📤 Full route payload:", JSON.stringify(payload, null, 2));
      
      // payload.pickupOrder = [...new Set(payload.pickupOrder)];
      const response = await axiosInstance.post("/routes", payload);

      if (response.data?.success || response.data) {
        toast.success(
          mode === "pickup-orders" && waypoints.length === 1
            ? "Pickup route saved! Waiting for rider acceptance."
            : "Route saved successfully!"
        );
        handleClearRoute();
        // Call the callback to refresh routes list
        onRouteCreated?.();
        // Refresh existing routes to update filtering
        await fetchExistingRoutes();
        // Refresh pharmacies or orders based on mode
        if (mode === "pickup-orders") {
          await fetchPharmacies();
        } else {
          await fetchOrders();
        }
      } else {
        toast.error("Failed to save route");
      }
    } catch (error: any) {
      console.error("Error saving route:", error);
      toast.error(error?.response?.data?.message || "Failed to save route");
    } finally {
      setLoading(false);
    }
  };

  // Handle route calculation callback
  const handleRouteFound = (distance: number, duration: number) => {
    setRouteDistance(distance);
    setRouteDuration(duration);
    // toast.success(`Route calculated: ${distance.toFixed(2)} km, ${Math.round(duration)} min`);
  };

  // Enhanced Order Popup Component
  const OrderPopup = ({
    order,
    orderNumber,
  }: {
    order: Order;
    orderNumber?: number;
  }) => {
    const [detailedOrder, setDetailedOrder] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      const loadDetailedOrder = async () => {
        setLoading(true);
        const details = await fetchDetailedOrder(order._id);
        setDetailedOrder(details);
        setLoading(false);
      };
      loadDetailedOrder();
    }, [order._id]);

    if (loading) {
      return (
        <div className="p-3 text-center">
          <GlobalLoader isLoading={true} message="Processing please wait..." subMessage="Please wait" />
          <p className="text-sm text-gray-600 mt-2">Loading details...</p>
        </div>
      );
    }

    return (
      <div className="p-4 max-w-md">
        <div className="space-y-4">
          {/* Header with Order Number */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {orderNumber && (
                <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                  {orderNumber}
                </div>
              )}
              <h3 className="font-bold text-gray-900 text-lg">
                {order.orderNumber || order.prescriptionNumber || "Order"}
              </h3>
            </div>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                mode === "orders" 
                  ? (order.deliveryStatus === "completed"
                      ? "bg-green-100 text-green-800"
                      : order.deliveryStatus === "delivered"
                      ? "bg-blue-100 text-blue-800"
                      : order.deliveryStatus === "in-route"
                      ? "bg-yellow-100 text-yellow-800"
                      : order.deliveryStatus === "planned-for-delivery"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-gray-100 text-gray-800")
                  : (order.pickupStatus === "completed"
                      ? "bg-green-100 text-green-800"
                      : order.pickupStatus === "picked-up"
                      ? "bg-blue-100 text-blue-800"
                      : order.pickupStatus === "pickup-requested"
                      ? "bg-yellow-100 text-yellow-800"
                      : order.pickupStatus === "assigned"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-gray-100 text-gray-800")
              }`}
            >
              {mode === "orders" 
                ? (order.deliveryStatus?.toUpperCase() || "UNKNOWN")
                : (order.pickupStatus?.toUpperCase() || "UNKNOWN")
              }
            </span>
          </div>

          {/* Service Type */}
          <div className="bg-blue-50 px-3 py-1 rounded-full inline-block">
            <span className="text-sm font-medium text-blue-800">
              Courier-RX
            </span>
          </div>

          {/* Customer Info */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-500" />
              <div>
                <p className="font-medium text-gray-900">
                  {order.customerName ||
                    order.patientName ||
                    "Unknown Customer"}
                </p>
                <p className="text-sm text-gray-600">
                  {order.customerPhone || order.patientPhone || "No phone"}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-700">
                  {order.customerAddress ||
                    order.patientAddress ||
                    order.address ||
                    "No address"}
                </p>
                {order.location?.latitude && order.location?.longitude && (
                  <p className="text-xs text-gray-500 mt-1">
                    📍 Coordinates: {order.location.latitude.toFixed(6)},{" "}
                    {order.location.longitude.toFixed(6)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Pharmacy Info */}
          {order.pharmacyName && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center space-x-2">
                <Package className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    From Pharmacy
                  </p>
                  <p className="text-sm text-gray-700">{order.pharmacyName}</p>
                </div>
              </div>
            </div>
          )}

          {/* Medicines */}
          {detailedOrder?.medicines && detailedOrder.medicines.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-800 flex items-center">
                <Package className="w-4 h-4 mr-1" />
                Medicines ({detailedOrder.medicines.length})
              </h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {detailedOrder.medicines.map((medicine: any, index: number) => (
                  <div key={index} className="bg-gray-50 p-2 rounded text-sm">
                    <p className="font-medium">{medicine.name}</p>
                    <p className="text-gray-600">
                      {medicine.quantity}x {medicine.dosage || medicine.potency}
                    </p>
                    {medicine.price && (
                      <p className="text-green-600 font-medium">
                        ${medicine.price}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delivery Info */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm font-medium">Delivery Date</p>
                <p className="text-sm text-gray-600">
                  {order.deliveryDate ||
                    detailedOrder?.deliveryDate ||
                    "Not specified"}
                </p>
              </div>
            </div>

            {((order as any).deliveryTime ||
              (detailedOrder as any)?.deliveryTime) && (
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Delivery Time</p>
                  <p className="text-sm text-gray-600">
                    {(order as any).deliveryTime ||
                      (detailedOrder as any)?.deliveryTime}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm font-medium">Priority</p>
                <p className="text-sm text-gray-600">
                  {order.priority || detailedOrder?.priority || "Standard"}
                </p>
              </div>
            </div>

            {/* Order Value */}
            {order.totalPrice && (
              <div className="flex items-center space-x-2">
                <Package className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Order Value</p>
                  <p className="text-sm text-green-600 font-semibold">
                    $
                    {typeof order.totalPrice === "number"
                      ? order.totalPrice.toFixed(2)
                      : order.totalPrice}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Status and Time */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Time:{" "}
                  {new Date().toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </p>
                <p className="text-sm text-gray-600">
                  Status: {mode === "orders" 
                    ? (order.deliveryStatus || "Pending")
                    : (order.pickupStatus || "Pending")
                  }
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Order #{orderNumber}</p>
                <p className="text-xs text-gray-400">Ready for delivery</p>
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          {detailedOrder?.notes && (
            <div className="bg-yellow-50 p-2 rounded">
              <p className="text-sm font-medium text-yellow-800">
                Special Instructions:
              </p>
              <p className="text-sm text-yellow-700">{detailedOrder.notes}</p>
            </div>
          )}

          {/* Action Button */}
          {isAddingWaypoints && (
            <Button 
              size="sm" 
              className="w-full mt-2" 
              onClick={() => handleMarkerClick(order)}
            >
              Add to Route
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Helper function to check if pharmacy is currently open
  const checkPharmacyOpen = (pharmacy: any) => {
    if (!pharmacy?.shiftTimings || pharmacy.shiftTimings.length === 0) {
      // Fallback to simple openingTime/closingTime check if no shiftTimings
      if (!pharmacy?.openingTime || !pharmacy?.closingTime) return null;
      
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      
      return currentTime >= pharmacy.openingTime && currentTime <= pharmacy.closingTime;
    }

    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    // Find shift for current day
    const todayShift = pharmacy.shiftTimings.find(
      (shift: any) => shift.day === currentDay
    );

    if (!todayShift) return null;

    // Check if pharmacy is open today
    if (!todayShift.isOpen) return false;

    // Check if current time is within opening hours
    return currentTime >= todayShift.openingTime && currentTime <= todayShift.closingTime;
  };

  // Helper function to get today's shift for display
  const getPharmacyTodayShift = (pharmacy: any) => {
    if (!pharmacy?.shiftTimings || pharmacy.shiftTimings.length === 0) {
      return { openingTime: pharmacy?.openingTime, closingTime: pharmacy?.closingTime };
    }
    
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const todayShift = pharmacy.shiftTimings.find(
      (shift: any) => shift.day === currentDay
    );
    
    return todayShift || { openingTime: pharmacy?.openingTime, closingTime: pharmacy?.closingTime };
  };

  // Enhanced Pharmacy Popup Component
  const PharmacyPopup = ({
    pharmacy,
    orderCount,
  }: {
    pharmacy: Pharmacy;
    orderCount: number;
  }) => {
    // Check if pharmacy is currently open
    const isPharmacyOpen = () => {
      if (!pharmacy.shiftTimings || pharmacy.shiftTimings.length === 0) {
        // Fallback to simple openingTime/closingTime check if no shiftTimings
        if (!pharmacy.openingTime || !pharmacy.closingTime) return null;
        
        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        
        return currentTime >= pharmacy.openingTime && currentTime <= pharmacy.closingTime;
      }

      const now = new Date();
      const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

      // Find shift for current day
      const todayShift = pharmacy.shiftTimings.find(
        (shift) => shift.day === currentDay
      );

      if (!todayShift) return null;

      // Check if pharmacy is open today
      if (!todayShift.isOpen) return false;

      // Check if current time is within opening hours
      return currentTime >= todayShift.openingTime && currentTime <= todayShift.closingTime;
    };

    const isOpen = isPharmacyOpen();
    
    // Get today's shift for display
    const getTodayShift = () => {
      if (!pharmacy.shiftTimings || pharmacy.shiftTimings.length === 0) {
        return { openingTime: pharmacy.openingTime, closingTime: pharmacy.closingTime };
      }
      
      const now = new Date();
      const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
      const todayShift = pharmacy.shiftTimings.find(
        (shift) => shift.day === currentDay
      );
      
      return todayShift || { openingTime: pharmacy.openingTime, closingTime: pharmacy.closingTime };
    };

    const todayShift = getTodayShift();

    return (
      <div className="p-3 max-w-sm">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center space-x-2">
              <h3 className="font-bold text-yellow-600 text-lg">
                {pharmacy.pharmacyName || pharmacy.name || "Pharmacy"}
              </h3>
              <div className="flex flex-row items-center space-x-2">
                <span className="text-sm text-gray-700">
                  {isOpen === null ? (
                    <>Open from {todayShift.openingTime} to {todayShift.closingTime}</>
                  ) : isOpen ? (
                    <span className="text-green-600 font-semibold">
                      ✓ Open - {todayShift.openingTime} to {todayShift.closingTime}
                    </span>
                  ) : (
                    <span className="text-red-600 font-semibold">
                      ✗ Closed
                    </span>
                  )}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Package className="w-4 h-4 text-gray-500" />
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                {orderCount} Orders
              </span>
            </div>
          </div>

          {/* Pharmacy Info */}
          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
              <div>
                <p className="text-sm text-gray-700">
                  {pharmacy.location?.address ||
                    pharmacy.address ||
                    "No address"}
                </p>
                {pharmacy.location?.latitude &&
                  pharmacy.location?.longitude && (
                  <p className="text-xs text-gray-500 mt-1">
                      📍 Coordinates: {pharmacy.location.latitude.toFixed(6)},{" "}
                      {pharmacy.location.longitude.toFixed(6)}
                  </p>
                )}
              </div>
            </div>

            {pharmacy.phone && (
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-500" />
                <p className="text-sm text-gray-700">{pharmacy.phone}</p>
              </div>
            )}

            {pharmacy.licenseNumber && (
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-gray-500" />
                <p className="text-sm text-gray-700">
                  License: {pharmacy.licenseNumber}
                </p>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="bg-blue-50 p-2 rounded">
            <p className="text-sm font-medium text-blue-800">
              Available for Pickup
            </p>
            <p className="text-sm text-blue-700">
              {orderCount} {orderCount === 1 ? "order" : "orders"} ready for
              collection
            </p>
          </div>

          {/* Action Button */}
          {isAddingWaypoints && (
            <Button 
              size="sm" 
              className="w-full mt-2" 
              onClick={() => {
                const point = getPharmacyPickupPoint(pharmacy);
                if (point) {
                  const pharmacyId = pharmacy._id || pharmacy.id || "";
                  const orderIds = pharmacyOrders.get(pharmacyId) || [];
                  
                  setWaypoints((prev) => [
                    ...prev,
                    {
                    lat: point.lat, 
                    lng: point.lng, 
                      address:
                        point.address ||
                        pharmacy.address ||
                        pharmacy.pharmacyName ||
                        pharmacy.name ||
                        "Pharmacy Location",
                      type: "pharmacy",
                    orderIds: orderIds, // Include order IDs
                    pharmacy: {
                      ...pharmacy,
                      id: pharmacyId,
                        name:
                          pharmacy.pharmacyName ||
                          pharmacy.name ||
                          "Unknown Pharmacy",
                      },
                    },
                  ]);
                  console.log("🏥 Added pharmacy waypoint:", {
                    name: pharmacy.pharmacyName || pharmacy.name,
                    phone: pharmacy.phone,
                    id: pharmacyId,
                    orderIds: orderIds,
                  });
                  toast.success(
                    `Added pharmacy with ${
                      orderIds.length
                    } order(s) to route #${waypoints.length + 1}`
                  );
                }
              }}
            >
              Add to Route
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative w-full h-[80vh]">
      {/* Map Container */}
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: "100%", width: "100%" }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Map Controller */}
        <MapController center={mapCenter} zoom={mapZoom} />

        {/* Fit Bounds Controller - auto-fits map to show all markers */}
        {(() => {
          const allLocations: Array<{ lat: number; lng: number }> = [];
          
          // Collect waypoints if they exist
          if (waypoints && waypoints.length > 0) {
            waypoints.forEach(wp => {
              allLocations.push({ lat: wp.lat, lng: wp.lng });
            });
          } else {
            // Collect pharmacy/order points when not viewing a saved route
            if (mode === "pickup-orders") {
              getPickupPharmacyPoints().forEach(({ point }) => {
                allLocations.push({ lat: point.lat, lng: point.lng });
              });
            } else if (mode === "orders") {
              orders.forEach(order => {
                const location = order.pickupLocation || order.deliveryLocation || order.customerLocation;
                if (location && location.latitude && location.longitude) {
                  allLocations.push({ lat: location.latitude, lng: location.longitude });
                }
              });
            }
          }
          
          return allLocations.length > 0 ? <FitBoundsController locations={allLocations} /> : null;
        })()}

        {/* Map Click Handler */}
        {!isViewOnly && (
          <MapClickHandler
            onMapClick={handleMapClick}
            isAddingWaypoints={isAddingWaypoints}
          />
        )}

        {/* Display markers depending on mode */}
        {mode === "pickup-orders"
          ? getPickupPharmacyPoints().map(({ id, point }) => {
              const pharmacy = pharmacies.find((p) => (p._id || p.id) === id);
              const orderCount = pharmacy
                ? pharmacyOrderCounts.get(pharmacy._id || pharmacy.id) || 0
                : 0;
          return (
            <Marker
                  key={id}
                  position={[point.lat, point.lng]}
                  icon={pickupIcon}
              eventHandlers={{
                    click: () => {
                      if (!isAddingWaypoints) {
                        // toast.info('Click "Create Route" to start adding waypoints');
                        return;
                      }
                      const pharmacyId = pharmacy?._id || pharmacy?.id || "";
                      const orderIds = pharmacyOrders.get(pharmacyId) || [];
                      
                      const waypoint: Waypoint = {
                        lat: point.lat,
                        lng: point.lng,
                        address:
                          point.address ||
                          pharmacy?.address ||
                          pharmacy?.pharmacyName ||
                          pharmacy?.name ||
                          "Pharmacy Location",
                        type: "pharmacy",
                        orderIds: orderIds, // Include order IDs
                        pharmacy: pharmacy
                          ? {
                          ...pharmacy,
                          id: pharmacyId,
                              name:
                                pharmacy.pharmacyName ||
                                pharmacy.name ||
                                "Unknown Pharmacy",
                            }
                          : undefined,
                      };
                      setWaypoints((prev) => [...prev, waypoint]);
                      console.log(
                        "🏥 Added pharmacy waypoint from marker:",
                        waypoint.pharmacy,
                        "orderIds:",
                        orderIds
                      );
                      // toast.success(`Added pharmacy with ${orderIds.length} order(s) #${waypoints.length + 1}`);
                    },
              }}
            >
              {!isAddingWaypoints && (
                <Popup>
                  {pharmacy ? (
                    <PharmacyPopup
                      pharmacy={pharmacy}
                      orderCount={orderCount}
                    />
                  ) : (
                    <div className="text-sm">
                      <p className="font-semibold text-blue-600">Pharmacy</p>
                      <p className="text-gray-700">
                        {point.address || "Pharmacy Location"}
                      </p>
                    </div>
                  )}
                </Popup>
              )}
            </Marker>
          );
            })
          : getFilteredOrders().map((order, index) => {
              const location = getOrderLocation(order, mode);
              if (!location) return null;
              
              // Create numbered order icon (blue circle with white number)
              const orderNumber = index + 1; // Start from 1
              const numberedOrderIcon = new L.DivIcon({
                className: "order-numbered-marker",
                html: `
                  <div style="
                    background: #3B82F6;
                    border: 3px solid white;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: bold;
                    font-size: 14px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                  ">
                    ${orderNumber}
                  </div>
                `,
                iconSize: [30, 30],
                iconAnchor: [15, 15],
                popupAnchor: [0, -15],
              });
              
              // Get current time for display
              const currentTime = new Date().toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });
              
              // Create label icon for time and order info
              const labelIcon = new L.DivIcon({
                className: "order-label-marker",
                html: `
                  <div style="
                    background: white;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    padding: 2px 6px;
                    font-size: 11px;
                    font-weight: 500;
                    color: #374151;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                    white-space: nowrap;
                  ">
                    ${currentTime}
                  </div>
                `,
                iconSize: [60, 20],
                iconAnchor: [30, 10],
                popupAnchor: [0, -10],
              });
              
              return (
                <>
                  {/* Main order marker with number */}
                  <Marker
                    key={order._id}
                    position={[location.lat, location.lng]}
                    icon={numberedOrderIcon}
                    eventHandlers={{ 
                      click: () => {
                        if (!isAddingWaypoints) {
                          // toast.info('Click "Create Route" to start adding waypoints');
                          return;
                        }
                        // Extract pharmacy information from populated order
                        let pharmacyInfo = undefined;
                        if (
                          order.pharmacyId &&
                          typeof order.pharmacyId === "object"
                        ) {
                          // Pharmacy is populated - spread all fields including shiftTimings
                          pharmacyInfo = {
                            ...order.pharmacyId,
                            id: order.pharmacyId._id || order.pharmacyId,
                            name:
                              order.pharmacyId.pharmacyName ||
                              order.pharmacyId.name,
                          };
                          console.log(
                            "✅ Extracted pharmacy info from populated order (marker click):",
                            pharmacyInfo
                          );
                        } else if (order.pharmacyName) {
                          // Fallback to pharmacy name only
                          pharmacyInfo = {
                            id: null,
                            name: order.pharmacyName,
                            phone: null,
                            email: null,
                            address: null,
                          };
                          console.log(
                            "⚠️ Using pharmacy name fallback (marker click):",
                            pharmacyInfo
                          );
                        } else {
                          console.log(
                            "❌ No pharmacy information found in order (marker click)"
                          );
                        }

                        const waypoint: Waypoint = {
                          lat: location.lat,
                          lng: location.lng,
                          address:
                            location.address ||
                            order.customerAddress ||
                            order.patientAddress ||
                            "Delivery Location",
                          orderId: order._id,
                          type: "delivery",
                          pharmacy: pharmacyInfo,
                          customer: {
                            name:
                              order.customerName ||
                              order.patientName ||
                              "Customer",
                            phone: order.customerPhone || order.patientPhone,
                            address:
                              order.customerAddress ||
                              order.patientAddress ||
                              location.address,
                          },
                        };
                        setWaypoints((prev) => [...prev, waypoint]);
                        console.log(
                          "👤 Added customer waypoint from order:",
                          waypoint.customer
                        );
                        // toast.success(`Added customer waypoint #${waypoints.length + 1}`);
                      },
                    }}
                  >
                    {!isAddingWaypoints && (
                      <Popup>
                        <OrderPopup order={order} orderNumber={orderNumber} />
                      </Popup>
                    )}
                  </Marker>
                  
                </>
              );
            })}

        {/* Display waypoint markers with numbers */}
        {waypoints.map((wp, index) => (
          <Marker
            key={`waypoint-${index}`}
            position={[wp.lat, wp.lng]}
            icon={createNumberedWaypointIcon(index + 1)}
            eventHandlers={
              !isViewOnly
                ? {
              click: () => {
                setWaypoints(waypoints.filter((_, i) => i !== index));
                // toast.info('Waypoint removed');
              },
                  }
                : {}
            }
          >
            <Popup>
              <div className="text-sm min-w-[250px]">
                <p className="font-semibold text-center p-1 text-green-600 bg-blue-50">
                  {/* {(() => {
                    // Check for pharmacy data in waypoint
                    if (wp.type === "pharmacy" || wp.pharmacy) {
                      const pharmacyData = wp.pharmacy;
                      if (
                        typeof pharmacyData === "object" &&
                        pharmacyData !== null
                      ) {
                        return (
                          `Pharmacy: ${pharmacyData.name}` ||
                          (pharmacyData as any).pharmacyName ||
                          "Pharmacy"
                        );
                      }
                    }
                    // Check for customer data
                    if (wp.type === "delivery" || wp.customer) {
                      const customerData = wp.customer;
                      if (
                        typeof customerData === "object" &&
                        customerData !== null
                      ) {
                        return customerData.name || "Customer";
                      }
                    }
                    // Final fallback
                    return `Waypoint #${index + 1}`;
                  })()} */}
                  {(wp.pharmacy || (wp.pickupOrder && wp.pickupOrder[0]?.pharmacy)) && (() => {
                    // Try waypoint.pharmacy first, then pickupOrder.pharmacy
                    const wpPharmacy = wp.pharmacy || (wp.pickupOrder && wp.pickupOrder[0]?.pharmacy);
                    if (!wpPharmacy) return null;
                    
                    // Try to get full pharmacy data from pharmacies array
                    const fullPharmacy = pharmacies.find(p => (p._id || p.id) === wpPharmacy.id);
                    const pharmacyData = fullPharmacy || wpPharmacy;
                    
                    const isOpen = checkPharmacyOpen(pharmacyData);
                    const todayShift = getPharmacyTodayShift(pharmacyData);
                    
                    return (
                      <div className="mt-2 space-y-1 bg-blue-50 p-2 rounded">
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-3 h-3 text-blue-600" />
                          <span className="text-xs font-large text-blue-900">
                            Pharmacy Name: {wpPharmacy.pharmacyName || wpPharmacy.name}
                          </span>
                        </div>
                        {/* Open/Closed Status */}
                        <div className="text-xs flex">
                          {isOpen === null ? (
                            <span className="text-gray-700">
                              Open from {todayShift.openingTime || 'N/A'} to {todayShift.closingTime || 'N/A'}
                            </span>
                          ) : isOpen ? (
                            <span className="text-green-600 font-semibold">
                              ✓ Open - {todayShift.openingTime || 'N/A'} to {todayShift.closingTime || 'N/A'}
                            </span>
                          ) : (
                            <span className="text-red-600 font-semibold">
                              ✗ Closeds
                            </span>
                          )}
                        </div>
                        {wpPharmacy.phone && (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-3 h-3 text-blue-600" />
                            <span className="text-xs font-large text-blue-800">
                              Phone: {wpPharmacy.phone}
                            </span>
                          </div>
                        )}
                        {wpPharmacy.email && (
                          <div className="flex items-center space-x-2">
                            <MailIcon className="w-3 h-3 text-blue-600" />
                            <span className="text-xs font-large text-blue-800">
                              Email: {wpPharmacy.email}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </p>

                {(wp.pharmacy || wp.customer) && (
                  <p className="text-xs text-blue-600 mb-1">
                    {wp.type === "pharmacy"
                      ? "🏥 Pharmacy"
                      : wp.type === "delivery"
                      ? "👤 Customer"
                      : "📍 Location"}
                  </p>
                )}
                <p className="text-gray-700">{wp.address || "Manual Point"}</p>
                {wp.orderId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Order ID: {wp.orderId}
                  </p>
                )}
                {wp.pharmacy &&
                  typeof wp.pharmacy === "object" &&
                  (wp.pharmacy as any).phone && (
                  <p className="text-xs text-gray-600 mt-1">
                    📞 {(wp.pharmacy as any).phone}
                  </p>
                )}
                {wp.customer &&
                  typeof wp.customer === "object" &&
                  wp.customer.phone && (
                  <p className="text-xs text-gray-600 mt-1">
                    📞 {wp.customer.phone}
                  </p>
                )}

                {/* Order Details Section for Map Popup */}
                {(() => {
                  // Find associated order data if available
                  let associatedOrder = null;
                  if (wp.orderId) {
                    associatedOrder = orders.find((o) => o._id === wp.orderId);
                  } else if (wp.orderIds && wp.orderIds.length > 0) {
                    // For pharmacy waypoints with multiple orders, show the first one as primary
                    associatedOrder = orders.find((o) =>
                      wp.orderIds?.includes(o._id)
                    );
                  }

                  // Show order details if available
                  const shouldShow = !!(
                    associatedOrder ||
                    (wp.orderIds && wp.orderIds.length > 0) ||
                    orders.length > 0
                  );
                  console.log(`🗺️ Map popup for waypoint ${index + 1}:`, {
                    associatedOrder: !!associatedOrder,
                    waypointOrderIds: wp.orderIds?.length || 0,
                    waypointType: wp.type,
                    ordersLength: orders.length,
                    shouldShow,
                  });
                  if (shouldShow) {
                    return (
                      <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                        <div className="flex items-center space-x-1 mb-2">
                          <Package className="w-3 h-3 text-yellow-600" />
                          <span className="text-xs font-medium text-yellow-900">
                            Order Details
                          </span>
                        </div>
                        
                        <div className="space-y-1">
                          {/* Order Number and Status */}
                          {wp.orderIds && wp.orderIds.length > 0 ? (
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-yellow-800">
                                Orders:
                              </span>
                              <span className="text-xs text-yellow-700 font-mono">
                                {wp.orderIds.length} order
                                {wp.orderIds.length > 1 ? "s" : ""}
                              </span>
                            </div>
                          ) : associatedOrder ? (
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-yellow-800">
                                Order #:
                              </span>
                              <span className="text-xs text-yellow-700 font-mono">
                                {associatedOrder.orderNumber ||
                                  associatedOrder.prescriptionNumber ||
                                  associatedOrder._id?.slice(-8)}
                              </span>
                            </div>
                          ) : orders.length > 0 ? (
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-yellow-800">
                                {mode === "pickup-orders"
                                  ? "Individual Orders:"
                                  : "Route Orders:"}
                              </span>
                              <span className="text-xs text-yellow-700 font-mono">
                                {orders.length} order
                                {orders.length > 1 ? "s" : ""}
                              </span>
                            </div>
                          ) : null}
                          
                          {/* Status */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-yellow-800">
                              Status:
                            </span>
                            <span
                              className={`text-xs px-1 py-0.5 rounded-full ${
                                wp.orderIds && wp.orderIds.length > 0
                                  ? "bg-blue-100 text-blue-800"
                                  : orders.length > 0
                                  ? "bg-blue-100 text-blue-800"
                                  : associatedOrder?.status === "delivered"
                                  ? "bg-green-100 text-green-800"
                                  : associatedOrder?.status === "in-transit"
                                  ? "bg-blue-100 text-blue-800"
                                  : associatedOrder?.status === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {wp.orderIds && wp.orderIds.length > 1
                                ? "Multiple Orders"
                                : orders.length > 0
                                ? mode === "pickup-orders"
                                  ? "Individual Orders"
                                  : "Route Orders"
                                : mode === "orders"
                                  ? (associatedOrder?.deliveryStatus || associatedOrder?.status || "Unknown")
                                  : (associatedOrder?.pickupStatus || associatedOrder?.status || "Unknown")}
                            </span>
                          </div>

                          {/* Order Value */}
                          {/* {(associatedOrder as any)?.totalValue && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-yellow-800">
                                Value:
                              </span>
                              <span className="text-xs text-yellow-700 font-semibold">
                                $
                                {(associatedOrder as any).totalValue.toFixed(2)}
                              </span>
                            </div>
                          )} */}

                          {/* Show waypoint-specific orders - only if waypoint has specific order IDs */}
                          {wp.orderIds && wp.orderIds.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs font-semibold text-yellow-900 mb-1.5">
                                {mode === "pickup-orders"
                                  ? "Individual Orders:"
                                  : "Route Orders:"}{" "}
                                ({wp.orderIds.length})
                              </div>
                              <div className="max-h-64 overflow-y-auto space-y-1.5">
                                {wp.orderIds.slice(0, 3).map((orderId, idx) => {
                                  const order = orders.find(
                                    (o) =>
                                      o._id === orderId ||
                                      (o as any).id === orderId
                                  );
                                  return (
                                    <div
                                      key={idx}
                                      className="bg-white border border-yellow-300 px-2 py-1.5 rounded-lg space-y-1 shadow-sm"
                                    >
                                      {/* Order Header */}
                                      <div className="flex justify-between items-start">
                                        <span className="font-bold text-xs text-gray-900">
                                          {order
                                            ? order.orderNumber ||
                                              order.prescriptionNumber ||
                                              order._id?.slice(-8)
                                            : `Order ${idx + 1}`}
                                        </span>
                                        <span
                                          className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${
                                            mode === "orders"
                                              ? (order?.deliveryStatus === "completed" || order?.deliveryStatus === "delivered"
                                                  ? "bg-green-100 text-green-700"
                                                  : order?.deliveryStatus === "in-route"
                                                  ? "bg-blue-100 text-blue-700"
                                                  : order?.deliveryStatus === "pending"
                                                  ? "bg-yellow-100 text-yellow-700"
                                                  : order?.deliveryStatus === "planned-for-delivery"
                                                  ? "bg-purple-100 text-purple-700"
                                                  : "bg-gray-100 text-gray-700")
                                              : (order?.pickupStatus === "completed"
                                                  ? "bg-green-100 text-green-700"
                                                  : order?.pickupStatus === "picked-up"
                                                  ? "bg-blue-100 text-blue-700"
                                                  : order?.pickupStatus === "pickup-requested"
                                                  ? "bg-yellow-100 text-yellow-700"
                                                  : order?.pickupStatus === "assigned"
                                                  ? "bg-purple-100 text-purple-700"
                                                  : "bg-gray-100 text-gray-700")
                                          }`}
                                        >
                                          {mode === "orders"
                                            ? (() => {
                                                console.log(`🔍 Order ${order?._id} full order data:`, order);
                                                console.log(`🔍 Order ${order?._id} available fields:`, {
                                                  deliveryStatus: order?.deliveryStatus,
                                                  status: order?.status,
                                                  pickupStatus: order?.pickupStatus
                                                });
                                                const deliveryStatus = order?.deliveryStatus || order?.status || "Unknown";
                                                console.log(`🔍 Order ${order?._id} deliveryStatus:`, deliveryStatus);
                                                return deliveryStatus;
                                              })()
                                            : (order?.pickupStatus || order?.status || "Unknown")}
                                        </span>
                                      </div>
                                      
                                      {/* Pickup Order Info */}
                                      {mode === "pickup-orders" &&
                                        (order as any)?.pickupOrderInfo && (
                                        <div className="text-[9px] text-blue-700 font-semibold bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                                            📦{" "}
                                            {
                                              (order as any).pickupOrderInfo
                                                .pickupOrderId
                                            }
                                        </div>
                                      )}
                                      
                                      {/* Customer Info */}
                                      {(order as any)?.customerName && (
                                        <div className="space-y-0.5">
                                          <div className="text-[10px] font-medium text-gray-700">
                                            👤 {(order as any).customerName}
                                          </div>
                                          {(order as any)?.customerPhone && (
                                            <div className="text-[9px] text-gray-500">
                                              📞 {(order as any).customerPhone}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Order Value & Items */}
                                      <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                                        {/* {(order as any)?.totalValue && (
                                          <span className="text-[10px] font-bold text-green-600">
                                            $
                                            {(order as any).totalValue.toFixed(
                                              2
                                            )}
                                          </span>
                                        )} */}
                                        {(order as any)?.medicines &&
                                          (order as any).medicines.length > 0
                                             && (
                                          <span className="text-[9px] text-gray-600">
                                              💊{" "}
                                              {(order as any).medicines.length}{" "}
                                              items
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Medicines Preview */}
                                      {(order as any)?.medicines &&
                                        (order as any).medicines.length > 0 && (
                                        <div className="space-y-0.5 pt-1">
                                            {(order as any).medicines
                                              .slice(0, 2)
                                              .map(
                                                (med: any, medIdx: number) => (
                                                  <div
                                                    key={medIdx}
                                                    className="flex justify-between text-[9px] text-gray-600 bg-gray-50 px-1 py-0.5 rounded"
                                                  >
                                                    <span className="truncate">
                                                      {med.name} ×{med.quantity}
                                                    </span>
                                                    {med.subTotal && (
                                                      <span className="font-medium ml-1">
                                                        $
                                                        {med.subTotal.toFixed(
                                                          2
                                                        )}
                                                      </span>
                                                    )}
                                            </div>
                                                )
                                              )}
                                            {(order as any).medicines.length >
                                              2 && (
                                            <div className="text-[9px] text-gray-400 text-center">
                                                +
                                                {(order as any).medicines
                                                  .length - 2}{" "}
                                                more
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Delivery Date & Time */}
                                      {order?.deliveryDate && (
                                        <div className="text-[9px] text-gray-500 flex items-center gap-2">
                                          <span>
                                            📅{" "}
                                            {new Date(
                                              order.deliveryDate
                                            ).toLocaleDateString()}
                                          </span>
                                          {(order as any)?.deliveryTime && (
                                            <span>
                                              🕐 {(order as any).deliveryTime}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Special Instructions */}
                                      {(order as any)?.specialInstructions && (
                                        <div className="bg-orange-50 border border-orange-200 px-1.5 py-1 rounded">
                                          <div className="text-[9px] font-semibold text-orange-700">
                                            ⚠️ Instructions
                                          </div>
                                          <div className="text-[9px] text-orange-600 mt-0.5">
                                            {(
                                              order as any
                                            ).specialInstructions.substring(
                                              0,
                                              80
                                            )}
                                            {(order as any).specialInstructions
                                              .length > 80
                                              ? "..."
                                              : ""}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Notes */}
                                      {(order as any)?.notes && (
                                        <div className="bg-blue-50 border border-blue-200 px-1.5 py-1 rounded">
                                          <div className="text-[9px] font-semibold text-blue-700">
                                            📝 Notes
                                          </div>
                                          <div className="text-[9px] text-blue-600 mt-0.5">
                                            {(order as any).notes.substring(
                                              0,
                                              80
                                            )}
                                            {(order as any).notes.length > 80
                                              ? "..."
                                              : ""}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {wp.orderIds.length > 3 && (
                                  <div className="text-xs text-gray-500 text-center py-1 bg-gray-50 rounded">
                                    +{wp.orderIds.length - 3} more orders
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Routing Machine */}
        {routeCreated && waypoints.length >= 2 && (
          <RoutingMachine
            waypoints={waypoints}
            onRouteFound={handleRouteFound}
          />
        )}
      </MapContainer>

      {/* Route Information Panel for View Mode */}
      {isViewOnly && selectedRoute && (
        <div className="absolute top-4 left-4 z-[1000] w-96 shadow-lg bg-white border border-gray-200 rounded-lg">
          {/* Route Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Route Details
              </h3>
              <Badge
                className={
                  selectedRoute.status === "active"
                    ? "bg-blue-100 text-blue-800"
                    : selectedRoute.status === "completed"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }
              >
                {selectedRoute.status?.replace("-", " ") || "inactive"}
              </Badge>
            </div>
            
            <div className="mt-2">
              <h4 className="font-medium text-gray-900">
                {selectedRoute.name || "Untitled Route"}
              </h4>
              <p className="text-sm text-gray-600">
                {selectedRoute.description || "No description"}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-3">
              <div>
                Waypoints:{" "}
                <span className="font-medium">{waypoints.length}</span>
              </div>
              <div>
                Orders: <span className="font-medium">{orders.length}</span>
              </div>
              <div>
                Distance:{" "}
                <span className="font-medium">
                  {routeDistance > 0
                    ? `${routeDistance.toFixed(2)} km`
                    : (selectedRoute as any)?.distance
                    ? `${(selectedRoute as any).distance.toFixed(2)} km`
                    : "-"}
                </span>
              </div>
              <div>
                ETA:{" "}
                <span className="font-medium">
                  {routeDistance > 0
                    ? `${Math.round(routeDuration)} min`
                    : (selectedRoute as any)?.estimatedTime
                    ? `${Math.round((selectedRoute as any).estimatedTime)} min`
                    : "-"}
                </span>
              </div>
            </div>
          </div>

          {/* Waypoint List */}
          <div className="max-h-96 overflow-y-auto">
            <div className="p-4">
              <h4 className="font-medium text-gray-900 mb-3">
                Waypoint Details
              </h4>
              <div className="space-y-3">
                {waypoints.map((waypoint, index) => {
                  // Find associated order data if available
                  console.log(`🔍 Processing waypoint ${index + 1}:`, {
                    waypoint,
                    ordersState: orders,
                    ordersLength: orders.length,
                  });
                  
                  let associatedOrder = null;
                  if (waypoint.orderId) {
                    associatedOrder = orders.find(
                      (o) => o._id === waypoint.orderId
                    );
                    console.log(
                      `🔍 Found associated order by orderId:`,
                      associatedOrder
                    );
                  } else if (
                    waypoint.orderIds &&
                    waypoint.orderIds.length > 0
                  ) {
                    // For pharmacy waypoints with multiple orders, show the first one as primary
                    associatedOrder = orders.find((o) =>
                      waypoint.orderIds?.includes(o._id)
                    );
                    console.log(
                      `🔍 Found associated order by orderIds:`,
                      associatedOrder
                    );
                  }
                  const currentTime = new Date().toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  });

                  return (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                    >
                      <div className="flex items-start space-x-3">
                        {/* Number Circle */}
                        <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {index + 1}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Type Badge */}
                          {waypoint.type && (
                            <div className="mb-2">
                              {waypoint.type === "pharmacy" && (
                                <div className="bg-blue-100 px-2 py-1 rounded-full inline-block">
                                  <span className="text-xs font-medium text-blue-800">
                                    🏥 Pharmacy
                                  </span>
                                </div>
                              )}
                              {waypoint.type === "delivery" && (
                                <div className="bg-green-100 px-2 py-1 rounded-full inline-block">
                                  <span className="text-xs font-medium text-green-800">
                                    👤 Customer
                                  </span>
                                </div>
                              )}
                              {waypoint.type === "other" && (
                                <div className="bg-gray-100 px-2 py-1 rounded-full inline-block">
                                  <span className="text-xs font-medium text-gray-800">
                                    📍 Location
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Order/Pharmacy/Customer Name */}
                          <h5 className="font-semibold text-gray-900 text-sm">
                            {(() => {
                              // Check for pharmacy data in waypoint
                              if (
                                waypoint.type === "pharmacy" ||
                                waypoint.pharmacy
                              ) {
                                const pharmacyData = waypoint.pharmacy;
                                if (
                                  typeof pharmacyData === "object" &&
                                  pharmacyData !== null
                                ) {
                                  return (
                                    pharmacyData.pharmacyName ||
                                    (pharmacyData as any).name ||
                                    "Pharmacy"
                                  );
                                }
                              }
                              // Check for customer data
                              if (
                                waypoint.type === "delivery" ||
                                waypoint.customer
                              ) {
                                const customerData = waypoint.customer;
                                if (
                                  typeof customerData === "object" &&
                                  customerData !== null
                                ) {
                                  return customerData.name || "Customer";
                                }
                              }
                              // Check for associated order
                              if (associatedOrder) {
                                return (
                                  associatedOrder.orderNumber ||
                                  associatedOrder.prescriptionNumber ||
                                  "Order"
                                );
                              }
                              // Final fallback
                              return `Waypoint ${index + 1}`;
                            })()}
                          </h5>
                          
                          {/* Pharmacy Open/Close Status - ONLY for pickup orders */}
                          {waypoint.type === "pharmacy" && (waypoint.pharmacy || (waypoint.pickupOrder && waypoint.pickupOrder[0]?.pharmacy)) && (
                            <div className="mt-2">
                              {(() => {
                                const pharmacyData = waypoint.pharmacy || (waypoint.pickupOrder && waypoint.pickupOrder[0]?.pharmacy);
                                if (!pharmacyData) return null;
                                
                                const isOpen = checkPharmacyOpen(pharmacyData);
                                const todayShift = getPharmacyTodayShift(pharmacyData);
                                
                                return (
                                  <div className="text-xs">
                                    {isOpen === null ? (
                                      <span className="text-gray-600">
                                        Open from {todayShift.openingTime || 'N/A'} to {todayShift.closingTime || 'N/A'}
                                      </span>
                                    ) : isOpen ? (
                                      <span className="text-green-600 font-semibold">
                                        ✓ Open - {todayShift.openingTime || 'N/A'} to {todayShift.closingTime || 'N/A'}
                                      </span>
                                    ) : (
                                      <span className="text-red-600 font-semibold">
                                        ✗ Closed
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          
                          {/* Address */}
                          <div className="mt-2">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {waypoint.address || "Manual Point"}
                            </p>
                            {waypoint.lat && waypoint.lng && (
                              <p className="text-xs text-gray-500 mt-1">
                                📍 {waypoint.lat.toFixed(6)},{" "}
                                {waypoint.lng.toFixed(6)}
                              </p>
                            )}
                          </div>
                          
                          {/* Pharmacy Info */}
                          {/* {waypoint.pharmacy && (
                            <div className="mt-2 space-y-1 bg-blue-50 p-2 rounded">
                              <div className="flex items-center space-x-2">
                                <MapPin className="w-3 h-3 text-blue-600" />
                                <span className="text-xs font-medium text-blue-900">
                                  {waypoint.pharmacy.name}
                                </span>
                              </div>
                              {waypoint.pharmacy.phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="w-3 h-3 text-blue-600" />
                                  <span className="text-xs text-blue-800">
                                    {waypoint.pharmacy.phone}
                                  </span>
                                </div>
                              )}
                              {waypoint.pharmacy.email && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-blue-600">
                                    📧
                                  </span>
                                  <span className="text-xs text-blue-800">
                                    {waypoint.pharmacy.email}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {waypoint.customer && (
                            <div className="mt-2 space-y-1 bg-green-50 p-2 rounded">
                              <div className="flex items-center space-x-2">
                                <User className="w-3 h-3 text-green-600" />
                                <span className="text-xs font-medium text-green-900">
                                  {waypoint.customer.name}
                                </span>
                              </div>
                              {waypoint.customer.phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="w-3 h-3 text-green-600" />
                                  <span className="text-xs text-green-800">
                                    {waypoint.customer.phone}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {!waypoint.customer &&
                            !waypoint.pharmacy &&
                            associatedOrder && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center space-x-2">
                                <User className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-600">
                                    {associatedOrder.customerName ||
                                      associatedOrder.patientName ||
                                      "Unknown Customer"}
                                </span>
                              </div>
                              {associatedOrder.customerPhone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="w-3 h-3 text-gray-500" />
                                    <span className="text-xs text-gray-600">
                                      {associatedOrder.customerPhone}
                                    </span>
                                </div>
                              )}
                            </div>
                            )} */}

                          {/* Order Details Section - Show if there's an associated order, waypoint has order IDs, or if we have any orders in the route */}
                          {(() => {
                            const shouldShow = !!(
                              associatedOrder ||
                              (waypoint.orderIds &&
                                waypoint.orderIds.length > 0) ||
                              orders.length > 0
                            );
                            console.log(
                              `🔍 Waypoint ${index + 1} order details check:`,
                              {
                              associatedOrder: !!associatedOrder,
                                waypointOrderIds:
                                  waypoint.orderIds?.length || 0,
                              waypointType: waypoint.type,
                              ordersLength: orders.length,
                                shouldShow,
                              }
                            );
                            return shouldShow;
                          })() && (
                            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <Package className="w-4 h-4 text-yellow-600" />
                                <span className="text-sm font-medium text-yellow-900">
                                  Order Details
                                </span>
                              </div>
                              
                              {/* Order Number and Status */}
                              <div className="space-y-2">
                                {/* Handle multiple orders for pharmacy waypoints */}
                                {waypoint.orderIds &&
                                waypoint.orderIds.length > 0 ? (
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-yellow-800">
                                      Orders:
                                    </span>
                                    <span className="text-xs text-yellow-700 font-mono">
                                      {waypoint.orderIds.length} order
                                      {waypoint.orderIds.length > 1 ? "s" : ""}
                                    </span>
                                  </div>
                                ) : associatedOrder ? (
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-yellow-800">
                                      Order #:
                                    </span>
                                    <span className="text-xs text-yellow-700 font-mono">
                                      {associatedOrder.orderNumber ||
                                        associatedOrder.prescriptionNumber ||
                                        associatedOrder._id?.slice(-8)}
                                    </span>
                                  </div>
                                ) : orders.length > 0 ? (
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-yellow-800">
                                      {mode === "pickup-orders"
                                        ? "Individual Orders:"
                                        : "Route Orders:"}
                                    </span>
                                    <span className="text-xs text-yellow-700 font-mono">
                                      {orders.length} order
                                      {orders.length > 1 ? "s" : ""}
                                    </span>
                                  </div>
                                ) : null}
                                
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-yellow-800">
                                    Status:
                                  </span>
                                  <span
                                    className={`text-xs px-2 py-1 rounded-full ${
                                      waypoint.orderIds &&
                                      waypoint.orderIds.length > 0
                                        ? "bg-blue-100 text-blue-800"
                                        : orders.length > 0
                                        ? "bg-blue-100 text-blue-800"
                                        : associatedOrder?.status ===
                                          "delivered"
                                        ? "bg-green-100 text-green-800"
                                        : associatedOrder?.status ===
                                          "in-transit"
                                        ? "bg-blue-100 text-blue-800"
                                        : associatedOrder?.status === "pending"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {waypoint.orderIds &&
                                    waypoint.orderIds.length > 1
                                      ? "Multiple Orders"
                                      : orders.length > 0
                                      ? mode === "pickup-orders"
                                        ? "Individual Orders"
                                        : "Route Orders"
                                      : mode === "orders"
                                        ? (associatedOrder?.deliveryStatus || associatedOrder?.status || "Unknown")
                                        : (associatedOrder?.pickupStatus || associatedOrder?.status || "Unknown")}
                                  </span>
                                </div>

                                {/* Order Value */}
                                {/* {(associatedOrder as any)?.totalValue && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-yellow-800">
                                      Total Value:
                                    </span>
                                    <span className="text-xs text-yellow-700 font-semibold">
                                      $
                                      {(
                                        associatedOrder as any
                                      ).totalValue.toFixed(2)}
                                    </span>
                                  </div>
                                )} */}

                                {/* Delivery Date & Time */}
                                {associatedOrder?.deliveryDate && (
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-medium text-yellow-800">
                                        Delivery Date:
                                      </span>
                                      <span className="text-xs text-yellow-700">
                                        {new Date(
                                          associatedOrder.deliveryDate
                                        ).toLocaleDateString()}
                                      </span>
                                    </div>
                                    {(associatedOrder as any)?.deliveryTime && (
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-yellow-800">
                                          Delivery Time:
                                        </span>
                                        <span className="text-xs text-yellow-700">
                                          {
                                            (associatedOrder as any)
                                              .deliveryTime
                                          }
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Medicines/Items */}
                                {(associatedOrder as any)?.medicines &&
                                  (associatedOrder as any).medicines.length >
                                    0 && (
                                  <div className="mt-2">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <span className="text-xs font-medium text-yellow-800">
                                          Medicines:
                                        </span>
                                        <span className="text-xs text-yellow-600">
                                          (
                                          {
                                            (associatedOrder as any).medicines
                                              .length
                                          }{" "}
                                          items)
                                        </span>
                                    </div>
                                    <div className="max-h-20 overflow-y-auto space-y-1">
                                        {(associatedOrder as any).medicines
                                          .slice(0, 3)
                                          .map(
                                            (
                                              medicine: any,
                                              medIndex: number
                                            ) => (
                                              <div
                                                key={medIndex}
                                                className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded"
                                              >
                                          <div className="flex justify-between">
                                                  <span className="font-medium">
                                                    {medicine.name}
                                                  </span>
                                                  <span className="text-yellow-600">
                                                    Qty: {medicine.quantity}
                                                  </span>
                                          </div>
                                          {medicine.subTotal && (
                                            <div className="text-right text-yellow-600">
                                                    $
                                                    {medicine.subTotal.toFixed(
                                                      2
                                                    )}
                                            </div>
                                          )}
                                        </div>
                                            )
                                          )}
                                        {(associatedOrder as any).medicines
                                          .length > 3 && (
                                        <div className="text-xs text-yellow-600 text-center">
                                            +
                                            {(associatedOrder as any).medicines
                                              .length - 3}{" "}
                                            more items
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Special Instructions */}
                                {(associatedOrder as any)
                                  ?.specialInstructions && (
                                  <div className="mt-2">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="text-xs font-medium text-yellow-800">
                                        Instructions:
                                      </span>
                                    </div>
                                    <div className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                                      {
                                        (associatedOrder as any)
                                          .specialInstructions
                                      }
                                    </div>
                                  </div>
                                )}

                                {/* Order Notes */}
                                {(associatedOrder as any)?.notes && (
                                  <div className="mt-2">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="text-xs font-medium text-yellow-800">
                                        Notes:
                                      </span>
                                    </div>
                                    <div className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded">
                                      {(associatedOrder as any).notes}
                                    </div>
                                  </div>
                                )}

                                {/* Show waypoint-specific orders - only if waypoint has specific order IDs */}
                                {waypoint.orderIds &&
                                  waypoint.orderIds.length > 0 && (
                                  <div className="mt-2">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className="text-xs font-medium text-yellow-800">
                                          {mode === "pickup-orders"
                                            ? "Individual Orders:"
                                            : "Route Orders:"}
                                      </span>
                                        <span className="text-xs text-yellow-600">
                                          ({waypoint.orderIds.length} for this
                                          waypoint)
                                        </span>
                                    </div>
                                    <div className="max-h-96 overflow-y-auto space-y-2">
                                        {waypoint.orderIds
                                          .slice(0, 5)
                                          .map((orderId, idx) => {
                                            const order = orders.find(
                                              (o) =>
                                                o._id === orderId ||
                                                (o as any).id === orderId
                                            );
                                        return (
                                              <div
                                                key={idx}
                                                className="bg-white border border-yellow-200 px-2.5 py-2 rounded-lg space-y-1.5 shadow-sm"
                                              >
                                            {/* Order Header */}
                                            <div className="flex justify-between items-start">
                                              <span className="font-semibold text-sm text-gray-900">
                                                    {order
                                                      ? order.orderNumber ||
                                                        order.prescriptionNumber ||
                                                        order._id?.slice(-8)
                                                      : `Order ${idx + 1}`}
                                              </span>
                                                  <span
                                                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                                      mode === "orders"
                                                        ? (order?.deliveryStatus === "completed" || order?.deliveryStatus === "delivered"
                                                            ? "bg-green-100 text-green-700"
                                                            : order?.deliveryStatus === "in-route"
                                                            ? "bg-blue-100 text-blue-700"
                                                            : order?.deliveryStatus === "pending"
                                                            ? "bg-yellow-100 text-yellow-700"
                                                            : order?.deliveryStatus === "planned-for-delivery"
                                                            ? "bg-purple-100 text-purple-700"
                                                            : "bg-gray-100 text-gray-700")
                                                        : (order?.pickupStatus === "completed"
                                                            ? "bg-green-100 text-green-700"
                                                            : order?.pickupStatus === "picked-up"
                                                            ? "bg-blue-100 text-blue-700"
                                                            : order?.pickupStatus === "pickup-requested"
                                                            ? "bg-yellow-100 text-yellow-700"
                                                            : order?.pickupStatus === "assigned"
                                                            ? "bg-purple-100 text-purple-700"
                                                            : "bg-gray-100 text-gray-700")
                                                    }`}
                                                  >
                                                    {mode === "orders"
                                                      ? (() => {
                                                          const deliveryStatus = order?.deliveryStatus || order?.status || "Unknown";
                                                          console.log(`🔍 Order ${order?._id} deliveryStatus (popup):`, deliveryStatus);
                                                          return deliveryStatus;
                                                        })()
                                                      : (order?.pickupStatus || order?.status || "Unknown")}
                                              </span>
                                            </div>

                                                {/* Pharmacy Information - ORDERS TAB */}
                                                {mode === "orders" && (
                                                  <>
                                                    {/* Try waypoint.pharmacy first */}
                                                    {waypoint.pharmacy && (() => {
                                                      const fullPharmacy = pharmacies.find(p => (p._id || p.id) === waypoint.pharmacy?.id);
                                                      const pharmacyData = fullPharmacy || waypoint.pharmacy;
                                                      const isOpen = checkPharmacyOpen(pharmacyData);
                                                      const todayShift = getPharmacyTodayShift(pharmacyData);
                                                      
                                                      return (
                                                        <div className="bg-purple-50 border border-purple-200 px-2 py-1.5 rounded space-y-0.5">
                                                          <div className="flex items-center space-x-1">
                                                            <span className="text-xs">
                                                              🏥
                                                            </span>
                                                            <span className="text-xs font-semibold text-purple-700">
                                                              {waypoint.pharmacy
                                                                .pharmacyName ||
                                                                waypoint.pharmacy
                                                                  .name ||
                                                                "Pharmacy"}
                                                            </span>
                                                          </div>
                                                          {/* Open/Closed Status */}
                                                          <div className="text-[9px]">
                                                            {isOpen === null ? (
                                                              <span className="text-gray-700">
                                                                Open {todayShift.openingTime || 'N/A'} - {todayShift.closingTime || 'N/A'}
                                                              </span>
                                                            ) : isOpen ? (
                                                              <span className="text-green-600 font-semibold">
                                                                ✓ Open - {todayShift.openingTime || 'N/A'} to {todayShift.closingTime || 'N/A'}
                                                              </span>
                                                            ) : (
                                                              <span className="text-red-600 font-semibold">
                                                                ✗ Closed
                                                              </span>
                                                            )}
                                                          </div>
                                                          {waypoint.pharmacy
                                                            .phone && (
                                                            <div className="flex items-center space-x-1">
                                                              <span className="text-[10px]">
                                                                📞
                                                              </span>
                                                              <span className="text-[10px] font-medium text-purple-600">
                                                                {
                                                                  waypoint
                                                                    .pharmacy
                                                                    .phone
                                                                }
                                                              </span>
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })()}

                                                    {/* Fallback to order.pharmacyId if waypoint.pharmacy not available */}
                                                    {!waypoint.pharmacy &&
                                                      (order as any)
                                                        ?.pharmacyId && (() => {
                                                          const pharmacyData = (order as any).pharmacyId;
                                                          const isOpen = checkPharmacyOpen(pharmacyData);
                                                          const todayShift = getPharmacyTodayShift(pharmacyData);
                                                          
                                                          return (
                                                            <div className="bg-purple-50 border border-purple-200 px-2 py-1.5 rounded space-y-0.5">
                                                              <div className="flex items-center space-x-1">
                                                                <span className="text-xs">
                                                                  🏥
                                                                </span>
                                                                <span className="text-xs font-semibold text-purple-700">
                                                                  {(order as any)
                                                                    .pharmacyId
                                                                    .pharmacyName ||
                                                                    (order as any)
                                                                      .pharmacyId
                                                                      .name ||
                                                                    "Pharmacy"}
                                                                </span>
                                                              </div>
                                                              {/* Open/Closed Status */}
                                                              <div className="text-[9px]">
                                                                {isOpen === null ? (
                                                                  <span className="text-gray-700">
                                                                    Open {todayShift.openingTime || 'N/A'} - {todayShift.closingTime || 'N/A'}
                                                                  </span>
                                                                ) : isOpen ? (
                                                                  <span className="text-green-600 font-semibold">
                                                                    ✓ Open - {todayShift.openingTime || 'N/A'} to {todayShift.closingTime || 'N/A'}
                                                                  </span>
                                                                ) : (
                                                                  <span className="text-red-600 font-semibold">
                                                                    ✗ Closed
                                                                  </span>
                                                                )}
                                                              </div>
                                                              {(order as any)
                                                                .pharmacyId
                                                                .phone && (
                                                                <div className="flex items-center space-x-1">
                                                                  <span className="text-[10px]">
                                                                    📞
                                                                  </span>
                                                                  <span className="text-[10px] font-medium text-purple-600">
                                                                    {
                                                                      (order as any)
                                                                        .pharmacyId
                                                                        .phone
                                                                    }
                                                                  </span>
                                                                </div>
                                                              )}
                                                            </div>
                                                          );
                                                        })()}

                                                    {/* Debug info - show when no pharmacy info is available */}
                                                    {!waypoint.pharmacy &&
                                                      !(order as any)
                                                        ?.pharmacyId && (
                                                        <div className="bg-gray-50 border border-gray-200 px-2 py-1.5 rounded space-y-0.5">
                                                          <div className="text-[10px] text-gray-500">
                                                            🏥 Pharmacy info not
                                                            available
                                                          </div>
                                                          <div className="text-[9px] text-gray-400">
                                                            Debug:
                                                            waypoint.pharmacy:{" "}
                                                            {waypoint.pharmacy
                                                              ? "exists"
                                                              : "null"}
                                                            , order.pharmacyId:{" "}
                                                            {(order as any)
                                                              ?.pharmacyId
                                                              ? "exists"
                                                              : "null"}
                                                          </div>
                                                        </div>
                                                      )}
                                                  </>
                                                )}
                                            
                                            {/* Pickup Order Info */}
                                                {mode === "pickup-orders" &&
                                                  (order as any)
                                                    ?.pickupOrderInfo && (
                                              <div className="text-xs text-blue-700 font-medium bg-blue-50 px-2 py-1 rounded border border-blue-200">
                                                      📦{" "}
                                                      {
                                                        (order as any)
                                                          .pickupOrderInfo
                                                          .pickupOrderId
                                                      }
                                              </div>
                                            )}
                                            
                                            {/* Customer Information */}
                                                {(order as any)
                                                  ?.customerName && (
                                              <div className="flex items-start space-x-1.5">
                                                    <span className="text-xs text-gray-500">
                                                      👤
                                                    </span>
                                                <div className="flex-1">
                                                      <div className="text-xs font-medium text-gray-700">
                                                        {
                                                          (order as any)
                                                            .customerName
                                                        }
                                                      </div>
                                                      {(order as any)
                                                        ?.customerPhone && (
                                                        <div className="text-[10px] text-gray-500">
                                                          {
                                                            (order as any)
                                                              .customerPhone
                                                          }
                                                        </div>
                                                      )}
                                                      {(order as any)
                                                        ?.customerAddress && (
                                                    <div className="text-[10px] text-gray-500 mt-0.5">
                                                          📍{" "}
                                                          {(
                                                            order as any
                                                          ).customerAddress.substring(
                                                            0,
                                                            60
                                                          )}
                                                          {(order as any)
                                                            .customerAddress
                                                            .length > 60
                                                            ? "..."
                                                            : ""}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                            
                                            {/* Order Value and Items */}
                                            <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                                                  {/* {(order as any)
                                                    ?.totalValue && (
                                                <span className="text-xs font-bold text-green-600">
                                                      $
                                                      {(
                                                        order as any
                                                      ).totalValue.toFixed(2)}
                                                </span>
                                              )} */}
                                                  {(order as any)?.medicines &&
                                                    (order as any).medicines
                                                      .length > 0 && (
                                                <span className="text-xs text-gray-600">
                                                        {
                                                          (order as any)
                                                            .medicines.length
                                                        }{" "}
                                                        item
                                                        {(order as any)
                                                          .medicines.length > 1
                                                          ? "s"
                                                          : ""}
                                                </span>
                                              )}
                                            </div>
                                            
                                            {/* Medicines List */}
                                                {(order as any)?.medicines &&
                                                  (order as any).medicines
                                                    .length > 0 && (
                                              <div className="space-y-0.5">
                                                      {(order as any).medicines
                                                        .slice(0, 3)
                                                        .map(
                                                          (
                                                            med: any,
                                                            medIdx: number
                                                          ) => (
                                                            <div
                                                              key={medIdx}
                                                              className="flex justify-between text-[10px] text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded"
                                                            >
                                                              <span>
                                                                💊 {med.name}{" "}
                                                                <span className="text-gray-400">
                                                                  ×
                                                                  {med.quantity}
                                                                </span>
                                                              </span>
                                                              {med.subTotal && (
                                                                <span className="font-medium">
                                                                  $
                                                                  {med.subTotal.toFixed(
                                                                    2
                                                                  )}
                                                                </span>
                                                              )}
                                                  </div>
                                                          )
                                                        )}
                                                      {(order as any).medicines
                                                        .length > 3 && (
                                                  <div className="text-[10px] text-gray-400 text-center">
                                                          +
                                                          {(order as any)
                                                            .medicines.length -
                                                            3}{" "}
                                                          more items
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            
                                            {/* Delivery Date & Time */}
                                            {order?.deliveryDate && (
                                              <div className="text-[10px] text-gray-500 flex items-center gap-2">
                                                    <span>
                                                      📅{" "}
                                                      {new Date(
                                                        order.deliveryDate
                                                      ).toLocaleDateString()}
                                                    </span>
                                                    {(order as any)
                                                      ?.deliveryTime && (
                                                      <span>
                                                        🕐{" "}
                                                        {
                                                          (order as any)
                                                            .deliveryTime
                                                        }
                                                      </span>
                                                )}
                                              </div>
                                            )}
                                            
                                            {/* Special Instructions */}
                                                {(order as any)
                                                  ?.specialInstructions && (
                                              <div className="bg-orange-50 border border-orange-200 px-2 py-1 rounded">
                                                    <div className="text-[10px] font-medium text-orange-700">
                                                      ⚠️ Instructions
                                                    </div>
                                                <div className="text-[10px] text-orange-600 mt-0.5">
                                                      {
                                                        (order as any)
                                                          .specialInstructions
                                                      }
                                                </div>
                                              </div>
                                            )}
                                            
                                            {/* Notes */}
                                            {(order as any)?.notes && (
                                              <div className="bg-blue-50 border border-blue-200 px-2 py-1 rounded">
                                                    <div className="text-[10px] font-medium text-blue-700">
                                                      📝 Notes
                                                    </div>
                                                <div className="text-[10px] text-blue-600 mt-0.5">
                                                  {(order as any).notes}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {waypoint.orderIds.length > 5 && (
                                        <div className="text-xs text-gray-500 text-center py-2">
                                            +{waypoint.orderIds.length - 5} more
                                            orders
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Show all orders for pharmacy waypoints with multiple orders */}
                                {waypoint.orderIds &&
                                  waypoint.orderIds.length > 1 && (
                                  <div className="mt-2">
                                    <div className="flex items-center space-x-2 mb-1">
                                        <span className="text-xs font-medium text-yellow-800">
                                          All Orders:
                                        </span>
                                        <span className="text-xs text-yellow-600">
                                          ({waypoint.orderIds.length} total)
                                        </span>
                                    </div>
                                    <div className="max-h-16 overflow-y-auto space-y-1">
                                        {waypoint.orderIds
                                          .slice(0, 3)
                                          .map((orderId, idx) => {
                                            const order = orders.find(
                                              (o) => o._id === orderId
                                            );
                                        return order ? (
                                              <div
                                                key={idx}
                                                className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded"
                                              >
                                            <div className="flex justify-between">
                                              <span className="font-medium">
                                                    {order.orderNumber ||
                                                      order.prescriptionNumber ||
                                                      `Order ${idx + 1}`}
                                              </span>
                                              <span className="text-yellow-600">
                                                    {order.status ||
                                                      order.pickupStatus ||
                                                      "Unknown"}
                                              </span>
                                            </div>
                                          </div>
                                        ) : (
                                              <div
                                                key={idx}
                                                className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded"
                                              >
                                            Order {idx + 1} (Loading...)
                                          </div>
                                        );
                                      })}
                                      {waypoint.orderIds.length > 3 && (
                                        <div className="text-xs text-yellow-600 text-center">
                                            +{waypoint.orderIds.length - 3} more
                                            orders
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Status and Time */}
                          <div className="mt-2 bg-white p-2 rounded border">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs font-medium text-gray-900">
                                  Time: {currentTime}
                                </p>
                                <p className="text-xs text-gray-600">
                                  Status:{" "}
                                  {mode === "orders"
                                    ? (associatedOrder?.deliveryStatus || "Ready")
                                    : (associatedOrder?.pickupStatus || "Ready")}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">
                                  Point #{index + 1}
                                </p>
                                <p className="text-xs text-gray-400">
                                  Ready for pickup
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Control Panel */}
      {!isViewOnly && (
        <Card className="absolute top-4 right-4 z-[1000] w-96 shadow-lg bg-white border border-gray-200">
        <CardContent className="p-4 space-y-3">
          {/* Address Search */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search address..."
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {suggestions.length > 0 || searching ? (
              <div className="absolute left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-[1100]">
                {suggestions.map((s, idx) => (
                  <button
                    key={`${s.lat}-${s.lon}-${idx}`}
                    onClick={() => handleSuggestionSelect(s)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    {s.display_name}
                  </button>
                ))}
                  {searching && (
                    <div className="px-3 py-2 text-xs text-gray-500">
                      Searching…
                    </div>
                  )}
              </div>
            ) : null}
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                  disabled={
                    currentLocation &&
                  Math.abs(mapCenter[0] - currentLocation[0]) < 0.001 && 
                    Math.abs(mapCenter[1] - currentLocation[1]) < 0.001
                  }
                onClick={() => {
                  if (!navigator.geolocation) return;
                  navigator.geolocation.getCurrentPosition((pos) => {
                    const { latitude, longitude } = pos.coords;
                    setMapCenter([latitude, longitude]);
                    setCurrentLocation([latitude, longitude]);
                      setWaypoints((prev) => [
                        ...prev,
                        {
                          lat: latitude,
                          lng: longitude,
                          address: "Current Location",
                        },
                      ]);
                  });
                }}
              >
                Use My Location
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <RouteIcon className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-lg">Route Control</h3>
          </div>

          {/* Status */}
          <div className="text-sm text-gray-600 space-y-1">
              {mode === "pickup-orders" ? (
                <>
                  <p>
                    📍 Available Pickups:{" "}
                    <span className="font-semibold">
                      {getPickupPharmacyPoints().length}
                    </span>
                  </p>
                {/* {pharmacies.length > getFilteredPharmacies().length && (
                  <p className="text-xs text-orange-600">
                      ⚠️ {pharmacies.length - getFilteredPharmacies().length}{" "}
                      pharmacy location(s) already in routes (hidden)
                  </p>
                )} */}
              </>
            ) : (
              <>
                  <p>
                    📍 Available Orders:{" "}
                    <span className="font-semibold">
                      {getFilteredOrders().length}
                    </span>
                  </p>
                {/* {orders.length > getFilteredOrders().length && (
                  <p className="text-xs text-orange-600">
                      ⚠️ {orders.length - getFilteredOrders().length} order(s)
                      already in any route (hidden)
                  </p>
                )} */}
              </>
            )}
              <p>
                🎯 Waypoints:{" "}
                <span className="font-semibold">{waypoints.length}</span>
              </p>
            {isAddingWaypoints && (
              <>
                  <p className="text-blue-600 font-medium">
                    ✨ Click markers or map to add points
                  </p>
                  {mode === "pickup-orders" && (
                  <p className="text-purple-600 text-xs font-medium">
                      ℹ️ Pickup routes: Select pharmacy → Rider location added
                      on acceptance
                  </p>
                )}
              </>
            )}
          </div>

          {/* Route Info */}
          {routeCreated && (
              <div
                className={`${
                  mode === "pickup-orders" && waypoints.length === 1
                    ? "bg-purple-50 border-purple-200"
                    : "bg-green-50 border-green-200"
                } border rounded-lg p-3 space-y-1`}
              >
                {mode === "pickup-orders" && waypoints.length === 1 ? (
                <div className="text-sm">
                    <p className="font-semibold text-purple-700 mb-1">
                      Pickup Route Ready
                    </p>
                  <p className="text-purple-600 text-xs">
                    Route will be completed when rider accepts
                  </p>
                </div>
              ) : routeDistance > 0 ? (
                <>
              <div className="flex items-center gap-2 text-sm">
                <Navigation className="w-4 h-4 text-green-600" />
                <span className="font-semibold text-green-700">
                  Distance: {routeDistance.toFixed(2)} km
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-green-600" />
                <span className="font-semibold text-green-700">
                  Duration: {Math.round(routeDuration)} min
                </span>
              </div>
                </>
              ) : (
                <p className="text-sm text-gray-600">Calculating route...</p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2">
            {!isAddingWaypoints && !routeCreated && (
              <div className="space-y-2">
                <Button
                  onClick={handleCreateRoute}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  disabled={loading}
                >
                  <Plus className="w-4 h-4" />
                  Create Route
                </Button>
                <Button
                  onClick={() => {
                      if (mode === "pickup-orders" && pharmacies.length > 0) {
                      focusMapOnPharmacies(pharmacies);
                      } else if (mode === "orders" && orders.length > 0) {
                      focusMapOnOrders(orders);
                    }
                  }}
                  variant="outline"
                  className="w-full"
                    disabled={
                      loading ||
                      (mode === "pickup-orders"
                        ? pharmacies.length === 0
                        : orders.length === 0)
                    }
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Focus on Markers
                </Button>
                {/* {waypoints.length > 0 && (
                  <Button
                    onClick={handleClearRoute}
                    variant="outline"
                    className="w-full"
                    disabled={loading}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Route
                  </Button>
                )} */}
              </div>
            )}

            {isAddingWaypoints && !routeCreated && (
              <Button
                onClick={handleGenerateRoute}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                  disabled={
                    mode === "pickup-orders"
                      ? waypoints.length < 1
                      : waypoints.length < 2
                  }
              >
                <Navigation className="w-4 h-4 mr-2" />
                  {mode === "pickup-orders" && waypoints.length === 1
                    ? "Create Pickup Route (1)"
                    : `Generate Route ${
                        waypoints.length >= 2 ? `(${waypoints.length})` : ""
                      }`}
              </Button>
            )}

            {routeCreated && (
              <Button
                onClick={handleSaveRoute}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-2 text-white" />
                  {loading ? "Saving..." : "Save Route"}
              </Button>
            )}

            {(waypoints.length > 0 || routeCreated) && (
              <Button
                onClick={handleClearRoute}
                variant="destructive"
                className="w-full bg-red-600 hover:bg-red-700"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Route
              </Button>
            )}
          </div>

          {/* Legend */}
          <div className="border-t pt-3 space-y-2 text-xs text-gray-600">
            <div className="flex items-center gap-2">
                <div
                  className={`w-4 h-4 rounded-full ${
                    mode === "pickup-orders" ? "bg-yellow-500" : "bg-blue-500"
                  }`}
                ></div>
                <span>
                  {mode === "pickup-orders"
                    ? "Pickup Locations"
                    : "Order Locations"}
                </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500"></div>
              <span>Route Waypoints</span>
            </div>
          </div>
        </CardContent>
        </Card>
      )}

      {/* Create Route Dialog */}
      {!isViewOnly && (
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[480px] bg-white border border-gray-200">
          <DialogHeader>
            <DialogTitle>Name your route</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={tempRouteName}
              onChange={(e) => setTempRouteName(e.target.value)}
              placeholder="Enter route name"
            />
          </div>
          <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmCreateRoute}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Start Creating
              </Button>
          </DialogFooter>
        </DialogContent>
        </Dialog>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black bg-opacity-30 z-[2000] flex items-center justify-center">
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="font-medium">Loading...</p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
